import { useState, useEffect, useCallback, useRef, Fragment } from "react";
import { C } from "../shared/theme";
import { ic } from "../shared/icons";
import { LogoImg } from "../shared/Logo";
import { Btn } from "../shared/Button";
// Logo lives directly inside /src, and this file is in /src/pages → one level up.
// If your file has a different name, change ONLY the path below.
import aiatusLogo from "../aiatus-logo.png";

// ─── BRAND ───
// AIATUS navy (matches the logo background exactly)
const NAVY = "#183D70";
const NAVY_DK = "#12305A";
const NAVY_BORDER = "#0d2547";
// Place aiatus-logo.png in your /public folder (served from PUBLIC_URL)
// Logo source — resolved by the bundler from the src import above
const LOGO_SRC = aiatusLogo;

// ─── CONFIG ───
const API_BASE = "";

// ─── USER SESSION ───
const getUser = () => ({
  id: localStorage.getItem("user_id"),
  email: localStorage.getItem("email"),
  firstName: localStorage.getItem("first_name") || "User",
  lastName: localStorage.getItem("last_name") || "",
  fullName: localStorage.getItem("full_name") || "User",
  companyId: localStorage.getItem("company_id"),
  companyName: localStorage.getItem("company_name") || "",
  companyCode: localStorage.getItem("company_code") || "",
  branchId: localStorage.getItem("branch_id"),
  branchName: localStorage.getItem("branch_name") || "",
  roleName: localStorage.getItem("role_name") || "",
  lastLogin: localStorage.getItem("last_login") || "",
  permissions: JSON.parse(localStorage.getItem("permissions") || "{}"),
});

// ─── HELPERS ───
const toBase64 = (file) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });

// Enterprise check — AIATUS users see full scores + metrics
const isEnterprise = () => (localStorage.getItem("company_code") || "").toUpperCase() === "AIATUS";

// Similarity band system
const getSimilarityBand = (sim) => {
  const pct = sim * 100;
  if (pct >= 88) return { label: "Very High", color: "#16a34a", bg: "#16a34a12" };
  if (pct >= 80) return { label: "High", color: "#22c55e", bg: "#22c55e12" };
  if (pct >= 70) return { label: "Mid High", color: "#84cc16", bg: "#84cc1612" };
  if (pct >= 60) return { label: "Mid", color: "#eab308", bg: "#eab30812" };
  if (pct >= 50) return { label: "Mid Low", color: "#f97316", bg: "#f9731612" };
  return { label: "Low", color: "#ef4444", bg: "#ef444412" };
};

// Display similarity: rounded score + band label
const SimDisplay = ({ sim, size = "lg" }) => {
  const band = getSimilarityBand(sim);
  const pct = Math.round((sim || 0) * 100);
  if (size === "lg") return <div style={{ textAlign: "center" }}>
    <div style={{ fontSize: 40, fontWeight: 800, color: band.color, fontFamily: "'Outfit'", lineHeight: 1 }}>{pct}%</div>
    <div style={{ fontSize: 14, fontWeight: 700, color: band.color, marginTop: 6 }}>{band.label}</div>
  </div>;
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
    <span style={{ fontSize: size === "md" ? 15 : 13, fontWeight: 700, color: band.color, background: band.bg, padding: "3px 12px", borderRadius: 6 }}>{pct}% · {band.label}</span>
  </span>;
};

const downloadJSON = (data, filename) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
};

const downloadCSV = (signatures, filename) => {
  const hasBbox = signatures.some(s => s.bbox && s.bbox[2] > 0);
  let csv = hasBbox ? "Signature_No,Page,X1,Y1,X2,Y2,Width,Height\n" : "Signature_No,Page,ID\n";
  signatures.forEach(s => {
    if (hasBbox && s.bbox) { csv += `${s.sigNo},${s.page||1},${s.bbox[0]},${s.bbox[1]},${s.bbox[2]},${s.bbox[3]},${s.bbox[2]-s.bbox[0]},${s.bbox[3]-s.bbox[1]}\n`; }
    else { csv += `${s.sigNo},${s.page||1},${s.id}\n`; }
  });
  const blob = new Blob([csv], { type: "text/csv" }); const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
};

// ─── REUSABLE COMPONENTS ───
function DocContextBar({ doc, sigCount, onClear }) {
  if (!doc) return null;
  return <div style={{ background: C.priPl, borderRadius: 10, padding: "10px 16px", marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between", border: `1px solid ${C.pri}20`, flexWrap: "wrap", gap: 8 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>{ic.file(C.pri)}<span style={{ fontSize: 14, fontWeight: 600, color: C.pri }}>{doc.name}</span>{doc.pageCount > 0 && <><span style={{ fontSize: 13, color: C.txM }}>·</span><span style={{ fontSize: 13, color: C.txS }}>{doc.pageCount} page{doc.pageCount > 1 ? "s" : ""}</span></>}{sigCount > 0 && <><span style={{ fontSize: 13, color: C.txM }}>·</span><span style={{ fontSize: 13, color: C.ok, fontWeight: 600 }}>{sigCount} signature{sigCount > 1 ? "s" : ""}</span></>}</div>
    <button onClick={onClear} style={{ background: "none", border: "none", color: C.txM, fontSize: 13, cursor: "pointer" }}>{ic.x()} Clear</button>
  </div>;
}

// Survives DocPreview remounts (tab components remount on parent re-render) so the
// preview doesn't reload/flicker every time. Keyed by document + page + aligned flag.
const _pageImgCache = {};

// Survive remounts for result screens whose local state would otherwise be wiped when
// a background op (auto-clean / auto-stamp) re-renders the parent.
let _stampDetCache = { file: null, preview: null, result: null };
let _docTypeCache = { file: null, preview: null, result: null };

function DocPreview({ doc, signatures, selectedPage, onPageChange, aligned }) {
  const isImage = doc?.type?.startsWith("image/");
  const docBase = doc?.name?.replace(/\.[^.]+$/, "") || "";
  const cacheKey = `${docBase}|${selectedPage}|${aligned ? 1 : 0}`;
  const cached = _pageImgCache[cacheKey];
  const [pageImg, setPageImg] = useState(cached?.url || null);
  const [imgLoading, setImgLoading] = useState(false);
  const [pageDims, setPageDims] = useState(cached?.dims || { w: 0, h: 0 });
  const [renderedDims, setRenderedDims] = useState({ w: 0, h: 0 });
  const imgRef = useRef(null);
  const pageSigs = (signatures || []).filter(s => s.page === selectedPage);

  const measureImg = () => {
    if (imgRef.current) {
      setRenderedDims({ w: imgRef.current.clientWidth, h: imgRef.current.clientHeight });
    }
  };

  // Load PDF/image page images from server (use aligned image if aligned)
  useEffect(() => {
    if (!docBase || !(doc?.pageCount > 0)) { setPageImg(null); return; }
    // For images without alignment, use local dataUrl (no need to fetch from server)
    if (isImage && !aligned) { setPageImg(null); return; }
    const ck = `${docBase}|${selectedPage}|${aligned ? 1 : 0}`;
    if (_pageImgCache[ck]) { setPageImg(_pageImgCache[ck].url); setPageDims(_pageImgCache[ck].dims); setImgLoading(false); return; }
    let cancelled = false;
    setImgLoading(true);
    const prefix = aligned ? "aligned_page" : "page";
    const base = `${API_BASE}/static/output/${encodeURIComponent(docBase)}/pages`;
    const tryLoad = (src, onFail) => {
      const img = new window.Image();
      img.onload = () => {
        const dims = { w: img.naturalWidth, h: img.naturalHeight };
        _pageImgCache[ck] = { url: src, dims }; // cache regardless of mount state so remounts read it instantly
        if (cancelled) return;
        setPageImg(src); setPageDims(dims); setImgLoading(false);
      };
      img.onerror = () => { if (!cancelled) onFail(); };
      img.src = src;
    };
    // Primary (aligned) → fall back to the plain page image → give up.
    tryLoad(`${base}/${prefix}_${selectedPage}.jpg`,
      () => tryLoad(`${base}/page_${selectedPage}.jpg`,
        () => { if (!cancelled) { setPageImg(null); setImgLoading(false); } }));
    return () => { cancelled = true; };
  }, [docBase, isImage, selectedPage, aligned, doc?.pageCount]);

  useEffect(() => {
    if (!docBase || !(doc?.pageCount > 1)) return;
    const prefix = aligned ? "aligned_page" : "page";
    const preload = (pg) => {
      if (pg >= 1 && pg <= doc.pageCount && pg !== selectedPage) {
        const img = new window.Image();
        img.src = `${API_BASE}/static/output/${encodeURIComponent(docBase)}/pages/${prefix}_${pg}.jpg`;
      }
    };
    preload(selectedPage + 1);
    preload(selectedPage + 2);
    preload(selectedPage - 1);
  }, [selectedPage, docBase, doc?.pageCount, aligned]);

  if (!doc) return null;

  const bboxOverlays = (origDims) => {
    const rw = renderedDims.w, rh = renderedDims.h;
    if (!rw || !rh || !origDims.w || !origDims.h) return null;
    const scaleX = rw / origDims.w, scaleY = rh / origDims.h;
    const colorFor = (t) => t === "customer" ? "#16a34a" : t === "institution" ? "#2563eb" : "#64748b";
    return pageSigs.filter(s => s.bbox && (s.bbox[2] > 0 && s.bbox[3] > 0)).map((s, i) => {
      const col = colorFor(s.signature_type);
      const label = s.signature_type === "customer" ? "Customer" : s.signature_type === "institution" ? "Institution" : "";
      return <div key={i} style={{ position: "absolute", left: s.bbox[0] * scaleX, top: s.bbox[1] * scaleY, width: (s.bbox[2] - s.bbox[0]) * scaleX, height: (s.bbox[3] - s.bbox[1]) * scaleY, border: `2px solid ${col}`, borderRadius: 3, background: `${col}18`, pointerEvents: "none" }}>
        <span style={{ position: "absolute", top: -18, left: 0, fontSize: 10, fontWeight: 700, color: C.wh, background: col, padding: "1px 6px", borderRadius: 3, whiteSpace: "nowrap" }}>#{s.sigNo}{label ? ` · ${label}` : ""}</span>
      </div>;
    });
  };

  return <div style={{ background: C.wh, borderRadius: 12, border: `1px solid ${C.bd}`, overflow: "hidden" }}>
    <div style={{ padding: "10px 16px", borderBottom: `1px solid ${C.bd}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <span style={{ fontSize: 14, fontWeight: 600, color: C.tx }}>Document Preview</span>
      {(doc.pageCount||0) > 1 && <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button onClick={() => onPageChange(Math.max(1, selectedPage-1))} style={{ background: C.off, border: `1px solid ${C.bd}`, borderRadius: 6, padding: "4px 8px", fontSize: 12, cursor: "pointer" }}>‹</button>
        <span style={{ fontSize: 13, color: C.txS }}>Page {selectedPage}/{doc.pageCount}</span>
        <button onClick={() => onPageChange(Math.min(doc.pageCount, selectedPage+1))} style={{ background: C.off, border: `1px solid ${C.bd}`, borderRadius: 6, padding: "4px 8px", fontSize: 12, cursor: "pointer" }}>›</button>
      </div>}
    </div>
    <div style={{ minHeight: 280, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, background: "#F8F9FB" }}>
      {imgLoading ? <div style={{ padding: 40 }}><div style={{ width: 32, height: 32, border: `3px solid ${C.bd}`, borderTopColor: C.pri, borderRadius: "50%", animation: "spin .7s linear infinite", margin: "0 auto" }} /></div> :
      pageImg ? <div style={{ position: "relative", display: "inline-block" }}><img ref={imgRef} src={pageImg} alt={`Page ${selectedPage}`} onLoad={measureImg} style={{ maxWidth: "100%", maxHeight: 600, borderRadius: 4, border: `1px solid ${C.bd}`, display: "block" }} />{pageDims.w > 0 && bboxOverlays(pageDims)}</div> :
      isImage ? <div style={{ position: "relative", display: "inline-block" }}><img ref={imgRef} src={doc.dataUrl} alt={doc.name} onLoad={measureImg} style={{ maxWidth: "100%", maxHeight: 600, borderRadius: 4, border: `1px solid ${C.bd}`, display: "block" }} />{bboxOverlays({ w: doc.naturalWidth || 800, h: doc.naturalHeight || 600 })}</div> :
      <div style={{ textAlign: "center", padding: 40 }}><div style={{ fontSize: 48, marginBottom: 12 }}>📄</div><div style={{ fontSize: 16, fontWeight: 600, color: C.tx }}>{doc.name}</div><div style={{ fontSize: 14, color: C.txM, marginTop: 4 }}>PDF document</div></div>}
    </div>
  </div>;
}

function SigCard({ sig, selected, onClick, imgOverride }) {
  const imgSrc = imgOverride || sig.imageBase64 || sig.data;
  return <div onClick={onClick} style={{ background: selected ? C.priPl : C.wh, borderRadius: 10, border: `1.5px solid ${selected ? C.pri : C.bd}`, padding: 14, cursor: "pointer", transition: "all .15s", display: "flex", gap: 14, alignItems: "center" }}>
    {imgSrc ? <img src={imgSrc} alt={`Sig ${sig.sigNo}`} style={{ width: 90, height: 52, objectFit: "contain", borderRadius: 6, background: C.off, border: `1px solid ${C.bd}` }} /> : <div style={{ width: 90, height: 52, borderRadius: 6, background: C.off, border: `1px solid ${C.bd}`, display: "flex", alignItems: "center", justifyContent: "center" }}>{ic.detect(C.txD)}</div>}
    <div style={{ flex: 1 }}><div style={{ fontSize: 15, fontWeight: 600, color: C.tx }}>Signature #{sig.sigNo}</div><div style={{ fontSize: 13, color: C.txM }}>Page {sig.page || 1}</div></div>
  </div>;
}

function UploadZone({ accept = "PDF, PNG, JPG, JPEG, TIFF", onFiles, compact = false }) {
  const [dragOver, setDragOver] = useState(false);
  const handleClick = () => {
    console.log(">>> UploadZone CLICKED");
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".pdf,.png,.jpg,.jpeg,.tiff,.tif";
    input.style.display = "none";
    input.onchange = (e) => {
      console.log(">>> FILE SELECTED:", e.target.files);
      if (e.target.files?.length > 0 && onFiles) {
        console.log(">>> Calling onFiles with", e.target.files[0].name);
        onFiles(Array.from(e.target.files));
      }
    };
    document.body.appendChild(input);
    input.click();
    console.log(">>> File dialog triggered");
    setTimeout(() => { try { document.body.removeChild(input); } catch(ex){} }, 10000);
  };
  return <div onClick={handleClick} onDrop={e => { e.preventDefault(); setDragOver(false); console.log(">>> DROP detected"); if (e.dataTransfer?.files?.length > 0 && onFiles) onFiles(Array.from(e.dataTransfer.files)); }} onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} style={{ border: `2px dashed ${dragOver ? C.pri : C.bd}`, borderRadius: 12, padding: compact ? 20 : 36, textAlign: "center", background: dragOver ? C.priPl : C.off, cursor: "pointer", transition: "all .2s" }}>
    <div style={{ color: C.pri, marginBottom: compact ? 4 : 10 }}>{ic.upload(C.pri)}</div>
    <p style={{ fontSize: compact ? 13 : 15, fontWeight: 600, color: C.tx, marginBottom: 4 }}>{compact ? "Drop or click" : "Drag & drop files here"}</p>
    <p style={{ fontSize: 12, color: C.txM }}>{accept}</p>
  </div>;
}

// Notification
// User feedback on a comparison result → POST /api/comparison_feedback
function ComparisonFeedback({ sig1, sig2, similarity, confidence, isMatch, user, notify }) {
  const [state, setState] = useState("idle"); // idle | wrong | sending | done
  const [shouldMatch, setShouldMatch] = useState(null);
  const [notes, setNotes] = useState("");

  const submit = async (feedback_result, sm) => {
    if (!sig1 || !sig2) { notify("Signature images not found", "warning"); return; }
    setState("sending");
    const fd = new FormData();
    fd.append("signature1_base64", sig1);
    fd.append("signature2_base64", sig2);
    fd.append("predicted_similarity", String(similarity ?? 0));
    fd.append("feedback_result", feedback_result);
    if (confidence) fd.append("model_confidence", confidence);
    fd.append("model_is_match", isMatch ? "true" : "false");
    if (sm === true || sm === false) fd.append("should_match", sm ? "true" : "false");
    if (notes.trim()) fd.append("notes", notes.trim());
    if (user?.companyId) fd.append("company_id", user.companyId);
    if (user?.branchId) fd.append("branch_id", user.branchId);
    if (user?.id) fd.append("user_id", user.id);
    try {
      const res = await fetch(`${API_BASE}/api/comparison_feedback`, { method: "POST", body: fd });
      if (!res.ok) throw new Error("Could not save");
      setState("done");
      notify("Feedback saved — thank you", "success");
    } catch (e) { notify("Feedback error: " + e.message, "error"); setState("idle"); }
  };

  if (state === "done") return <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px solid ${C.bdLt}`, fontSize: 13, fontWeight: 600, color: C.ok }}>✓ Your feedback has been saved. Thank you.</div>;

  const btn = (bg, col, brd) => ({ padding: "7px 16px", borderRadius: 8, border: `1px solid ${brd}`, background: bg, color: col, fontSize: 13, fontWeight: 700, cursor: "pointer" });

  return <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px solid ${C.bdLt}` }}>
    <div style={{ fontSize: 13, fontWeight: 600, color: C.txS, marginBottom: 8 }}>Is this result correct?</div>
    {state !== "wrong" ? <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
      <button onClick={() => submit("correct", null)} disabled={state === "sending"} style={btn(`${C.ok}12`, C.ok, `${C.ok}40`)}>✓ Correct</button>
      <button onClick={() => setState("wrong")} disabled={state === "sending"} style={btn(`${C.err}12`, C.err, `${C.err}40`)}>✗ Incorrect</button>
    </div> : <div>
      <div style={{ fontSize: 12, color: C.txM, marginBottom: 6 }}>What should it have been?</div>
      <div style={{ display: "flex", gap: 8, marginBottom: 8, justifyContent: "center" }}>
        <button onClick={() => setShouldMatch(true)} style={btn(shouldMatch === true ? `${C.ok}15` : C.wh, shouldMatch === true ? C.ok : C.txS, C.bd)}>Should match</button>
        <button onClick={() => setShouldMatch(false)} style={btn(shouldMatch === false ? `${C.err}15` : C.wh, shouldMatch === false ? C.err : C.txS, C.bd)}>Should not match</button>
      </div>
      <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Note (optional)" rows={2} style={{ width: "100%", padding: "8px 10px", border: `1px solid ${C.bd}`, borderRadius: 8, fontSize: 13, resize: "vertical", fontFamily: "inherit" }} />
      <div style={{ display: "flex", gap: 8, marginTop: 8, justifyContent: "center" }}>
        <button onClick={() => submit("incorrect", shouldMatch)} disabled={state === "sending"} style={btn(C.pri, C.wh, C.pri)}>{state === "sending" ? "Sending..." : "Send"}</button>
        <button onClick={() => { setState("idle"); setShouldMatch(null); setNotes(""); }} style={btn(C.off, C.txM, C.bd)}>Cancel</button>
      </div>
    </div>}
  </div>;
}

function Toast({ msg, type, onClose }) {
  if (!msg) return null;
  const bg = type === "success" ? C.ok : type === "error" ? C.err : C.warn;
  return <div style={{ position: "fixed", top: 74, right: 24, zIndex: 999, background: bg, color: C.wh, padding: "12px 20px", borderRadius: 10, fontSize: 14, fontWeight: 600, boxShadow: "0 4px 20px rgba(0,0,0,.15)", display: "flex", alignItems: "center", gap: 10, animation: "fi .3s ease" }}>
    {msg} <button onClick={onClose} style={{ background: "none", border: "none", color: C.wh, fontSize: 18, cursor: "pointer" }}>×</button>
  </div>;
}

// Isolated toast host: notifications go through a module-level emitter so calling
// notify() re-renders ONLY this host — not DashboardPage. That prevents the tab
// components (which are re-created on each parent render) from remounting and
// losing their local state every time a toast fires.
let _toastListener = null;
function emitToast(msg, type) { if (_toastListener) _toastListener(msg, type); }
function ToastHost() {
  const [t, setT] = useState({ msg: "", type: "" });
  const timer = useRef(null);
  useEffect(() => {
    _toastListener = (msg, type) => {
      setT({ msg, type });
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setT({ msg: "", type: "" }), 4000);
    };
    return () => { _toastListener = null; if (timer.current) clearTimeout(timer.current); };
  }, []);
  return <Toast msg={t.msg} type={t.type} onClose={() => setT({ msg: "", type: "" })} />;
}

// ═══════════════════════════════════════
// CLOCK (isolated to prevent parent re-renders)
// ═══════════════════════════════════════
function Clock() {
  const [time, setTime] = useState("");
  useEffect(() => { const u = () => setTime(new Date().toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" })); u(); const i = setInterval(u, 1000); return () => clearInterval(i); }, []);
  return <span>{time}</span>;
}

// ═══════════════════════════════════════
// PAGE WRAPPER (must be outside DashboardPage)
// Supports an optional `ver` prop — a version string or array of strings
// shown as badges next to the page title.
// ═══════════════════════════════════════
const P = ({ title, sub: s, children: ch }) => {
  return <>
    <div style={{ marginBottom: 22 }}>
      <h1 style={{ fontFamily: "'Outfit'", fontSize: 34, fontWeight: 800, color: C.tx, letterSpacing: -0.5 }}>{title}</h1>
      {s && <p style={{ fontSize: 17, color: C.txM, marginTop: 7 }}>{s}</p>}
    </div>
    {ch}
  </>;
};

// ═══════════════════════════════════════
// SEARCH TAB (must be outside DashboardPage to prevent remount loop)
// ═══════════════════════════════════════
function SearchTab({ user, notify }) {
  const [sigList, setSigList] = useState([]);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [sigPage, setSigPage] = useState(1);
  const pageSize = 10;
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editFields, setEditFields] = useState({});

  const fetchSigs = async (query, pg) => {
    setLoading(true);
    const params = new URLSearchParams({ search: query || "", page: pg.toString(), size: pageSize.toString() });
    if (user.companyId) params.append("company_id", user.companyId);
    try {
      const res = await fetch(`${API_BASE}/api/signatures?${params}`);
      const data = await res.json();
      setSigList(data.signatures || []);
      setTotal(data.total || 0);
    } catch (e) { notify("Failed to load signatures", "error"); }
    setLoading(false);
  };

  useEffect(() => { fetchSigs(searchQuery, sigPage); }, [searchQuery, sigPage]);

  const handleSave = async (sig) => {
    const form = new FormData();
    form.append("customer_id", editFields.customer_id || sig.customer_id || "");
    form.append("tckno", editFields.tckno || sig.tckno || "");
    form.append("vkn", editFields.vkn || sig.vkn || "");
    try {
      const res = await fetch(`${API_BASE}/api/signatures/${sig.id}`, { method: "PUT", body: form });
      if (!res.ok) throw new Error("Update failed");
      notify("Signature updated", "success");
      setEditingId(null); setEditFields({});
      fetchSigs(searchQuery, sigPage);
    } catch (e) { notify(e.message, "error"); }
  };

  const handleDelete = async (sig) => {
    if (!window.confirm(`Delete signature #${sig.id}?`)) return;
    try {
      const res = await fetch(`${API_BASE}/api/signatures/${sig.id}`, { method: "DELETE", headers: { "Content-Type": "application/json" } });
      if (!res.ok) throw new Error("Delete failed");
      notify("Signature deleted", "success");
      fetchSigs(searchQuery, sigPage);
    } catch (e) { notify(e.message, "error"); }
  };

  const totalPages = Math.ceil(total / pageSize);

  return <P title="Signature Search" sub="Browse, search, edit and manage registered signatures">
    <div style={{ background: C.wh, borderRadius: 12, border: `1px solid ${C.bd}`, overflow: "hidden" }}>
      <div style={{ padding: "14px 18px", borderBottom: `1px solid ${C.bd}`, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 200, position: "relative" }}>
          <div style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }}>{ic.search(C.txD)}</div>
          <input value={searchQuery} onChange={e => { setSearchQuery(e.target.value); setSigPage(1); }} placeholder="Search by Customer ID, TCKNO, VKN..." style={{ width: "100%", padding: "10px 12px 10px 38px", background: C.off, border: `1px solid ${C.bd}`, borderRadius: 8, fontSize: 14, color: C.tx }} />
        </div>
        <span style={{ fontSize: 13, color: C.txM }}>{total} signature{total !== 1 ? "s" : ""} total</span>
      </div>
      <div style={{ overflowX: "auto", overflowY: "visible" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 700 }}>
          <thead><tr style={{ borderBottom: `1px solid ${C.bd}`, background: C.off }}>
            {["ID", "Signature", "Customer ID", "TCKNO", "VKN", "Created", "Actions"].map(h => <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: 12, fontWeight: 600, color: C.txD, textTransform: "uppercase" }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={7} style={{ padding: 40, textAlign: "center", color: C.txM }}>Loading...</td></tr> :
            sigList.length === 0 ? <tr><td colSpan={7} style={{ padding: 40, textAlign: "center", color: C.txM }}>No signatures found</td></tr> :
            sigList.map(sig => {
              const isEditing = editingId === sig.id;
              return <tr key={sig.id} style={{ borderBottom: `1px solid ${C.bdLt}` }}>
                <td style={{ padding: "10px 14px", fontSize: 14, fontFamily: "'JetBrains Mono'", color: C.pri, fontWeight: 500 }}>{sig.id}</td>
                <td style={{ padding: "10px 14px", position: "relative" }}>
                  {sig.signature_base64 && <img src={sig.signature_base64.startsWith("data:") ? sig.signature_base64 : `data:image/png;base64,${sig.signature_base64}`} alt="" style={{ width: 120, height: 60, objectFit: "contain", borderRadius: 6, background: C.off, border: `1px solid ${C.bd}`, cursor: "zoom-in", transition: "transform .2s ease, z-index 0s", position: "relative", zIndex: 1 }} onMouseOver={e => { e.currentTarget.style.transform = "scale(2.2)"; e.currentTarget.style.zIndex = "50"; }} onMouseOut={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.zIndex = "1"; }} />}
                </td>
                <td style={{ padding: "10px 14px" }}>
                  {isEditing ? <input value={editFields.customer_id ?? sig.customer_id ?? ""} onChange={e => setEditFields(p => ({ ...p, customer_id: e.target.value }))} style={{ width: "100%", padding: "6px 8px", border: `1px solid ${C.pri}`, borderRadius: 6, fontSize: 13 }} /> : <span style={{ fontSize: 14 }}>{sig.customer_id || "—"}</span>}
                </td>
                <td style={{ padding: "10px 14px" }}>
                  {isEditing ? <input value={editFields.tckno ?? sig.tckno ?? ""} onChange={e => setEditFields(p => ({ ...p, tckno: e.target.value.replace(/\D/g, "").slice(0, 11) }))} style={{ width: 100, padding: "6px 8px", border: `1px solid ${C.pri}`, borderRadius: 6, fontSize: 13 }} /> : <span style={{ fontSize: 14 }}>{sig.tckno || "—"}</span>}
                </td>
                <td style={{ padding: "10px 14px" }}>
                  {isEditing ? <input value={editFields.vkn ?? sig.vkn ?? ""} onChange={e => setEditFields(p => ({ ...p, vkn: e.target.value.replace(/\D/g, "").slice(0, 10) }))} style={{ width: 90, padding: "6px 8px", border: `1px solid ${C.pri}`, borderRadius: 6, fontSize: 13 }} /> : <span style={{ fontSize: 14 }}>{sig.vkn || "—"}</span>}
                </td>
                <td style={{ padding: "10px 14px", fontSize: 12, color: C.txM }}>{sig.created_at ? new Date(sig.created_at).toLocaleDateString("en-GB") : "—"}</td>
                <td style={{ padding: "10px 14px" }}>
                  <div style={{ display: "flex", gap: 6 }}>
                    {isEditing ? <>
                      <button onClick={() => handleSave(sig)} style={{ padding: "5px 12px", borderRadius: 6, border: "none", background: C.ok, color: C.wh, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Save</button>
                      <button onClick={() => { setEditingId(null); setEditFields({}); }} style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${C.bd}`, background: C.wh, color: C.txM, fontSize: 12, cursor: "pointer" }}>Cancel</button>
                    </> : <>
                      <button onClick={() => { setEditingId(sig.id); setEditFields({ customer_id: sig.customer_id, tckno: sig.tckno, vkn: sig.vkn }); }} style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${C.bd}`, background: C.wh, color: C.pri, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Edit</button>
                      <button onClick={() => handleDelete(sig)} style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${C.err}30`, background: `${C.err}08`, color: C.err, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Delete</button>
                    </>}
                  </div>
                </td>
              </tr>;
            })}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && <div style={{ padding: "12px 18px", borderTop: `1px solid ${C.bd}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 13, color: C.txM }}>Page {sigPage} of {totalPages}</span>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={() => setSigPage(p => Math.max(1, p - 1))} disabled={sigPage <= 1} style={{ padding: "6px 12px", borderRadius: 6, border: `1px solid ${C.bd}`, background: C.wh, fontSize: 13, cursor: "pointer" }}>← Previous</button>
          <button onClick={() => setSigPage(p => Math.min(totalPages, p + 1))} disabled={sigPage >= totalPages} style={{ padding: "6px 12px", borderRadius: 6, border: `1px solid ${C.bd}`, background: C.wh, fontSize: 13, cursor: "pointer" }}>Next →</button>
        </div>
      </div>}
    </div>
  </P>;
}

// ═══════════════════════════════════════
// ALIGN TAB (must be outside DashboardPage to prevent state loss on re-render)
// ═══════════════════════════════════════
function AlignTab({ notify }) {
  const [aDoc, setADoc] = useState(null);
  const [aligning, setAligning] = useState(false);
  const [result, setResult] = useState(null);
  const [viewPage, setViewPage] = useState(1);
  const [origImg, setOrigImg] = useState(null);
  const [alignedImg, setAlignedImg] = useState(null);
  const [imgLoading, setImgLoading] = useState(false);

  const docBase = aDoc?.name?.replace(/\.[^.]+$/, "") || "";

  // Load original + aligned page images from server after alignment
  useEffect(() => {
    if (!result || !docBase || viewPage < 1) { setOrigImg(null); setAlignedImg(null); return; }
    let cancelled = false;
    setImgLoading(true);
    setOrigImg(null); setAlignedImg(null);
    let loaded = 0;
    const done = () => { loaded++; if (loaded >= 2 && !cancelled) setImgLoading(false); };

    // Original page
    const oUrl = `${API_BASE}/static/output/${encodeURIComponent(docBase)}/pages/page_${viewPage}.jpg`;
    const oImg = new window.Image();
    oImg.onload = () => { if (!cancelled) setOrigImg(oUrl); done(); };
    oImg.onerror = () => { if (!cancelled) setOrigImg(null); done(); };
    oImg.src = oUrl;

    // Aligned page
    const aUrl = `${API_BASE}/static/output/${encodeURIComponent(docBase)}/pages/aligned_page_${viewPage}.jpg`;
    const aImg = new window.Image();
    aImg.onload = () => { if (!cancelled) setAlignedImg(aUrl); done(); };
    aImg.onerror = () => { if (!cancelled) setAlignedImg(null); done(); };
    aImg.src = aUrl;

    return () => { cancelled = true; };
  }, [result, docBase, viewPage]);

  const handleAlign = async () => {
    if (!aDoc?.file) return;
    setAligning(true);
    setResult(null); setOrigImg(null); setAlignedImg(null);
    const formData = new FormData();
    formData.append("pdf", aDoc.file);
    formData.append("filename", aDoc.file.name);
    try {
      const res = await fetch(`${API_BASE}/api/align_document`, { method: "POST", body: formData });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || "Alignment failed"); }
      const data = await res.json();
      setResult(data);
      setViewPage(1);
      notify((data.rotated_pages || 0) > 0 ? `Aligned — ${data.rotated_pages}/${data.page_count || 1} page(s) rotated` : "Document is already aligned (no rotation needed)", "success");
    } catch (e) {
      notify("Alignment failed: " + e.message, "error");
    }
    setAligning(false);
  };

  const downloadPDF = () => {
    if (!docBase) return;
    window.open(`${API_BASE}/static/output/${encodeURIComponent(docBase)}/aligned.pdf`, "_blank");
  };

  const downloadPage = () => {
    if (!alignedImg) return;
    const a = document.createElement("a");
    a.href = alignedImg;
    a.download = `${docBase}_aligned_page_${viewPage}.jpg`;
    a.click();
  };

  // Page navigation (shared between left and right)
  const PageNav = () => result && result.page_count > 1 ? <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 16, marginTop: 8 }}>
    <button onClick={() => setViewPage(p => Math.max(1, p - 1))} disabled={viewPage <= 1} style={{ background: C.off, border: `1px solid ${C.bd}`, borderRadius: 6, padding: "4px 10px", fontSize: 12, cursor: "pointer" }}>‹</button>
    <span style={{ fontSize: 13, color: C.txS }}>Page {viewPage} / {result.page_count}</span>
    <button onClick={() => setViewPage(p => Math.min(result.page_count, p + 1))} disabled={viewPage >= result.page_count} style={{ background: C.off, border: `1px solid ${C.bd}`, borderRadius: 6, padding: "4px 10px", fontSize: 12, cursor: "pointer" }}>›</button>
  </div> : null;

  return <P title="Document Alignment" sub="Auto-correct skewed, rotated or distorted documents">
    {/* Rotation info bar */}
    {result && <div style={{ padding: "10px 14px", background: (result.rotated_pages || 0) === 0 ? `${C.ok}10` : C.priPl, borderRadius: 8, marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {ic.align((result.rotated_pages || 0) === 0 ? C.ok : C.pri)}
        <div>
          <span style={{ fontSize: 13, fontWeight: 600, color: (result.rotated_pages || 0) === 0 ? C.ok : C.pri }}>
            {(result.rotated_pages || 0) === 0 ? "Already aligned — no rotation needed" : `${result.rotated_pages} page(s) rotated`}
          </span>
          <div style={{ fontSize: 12, color: C.txM, marginTop: 1 }}>{result.page_count} page{result.page_count > 1 ? "s" : ""} processed</div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        {result.has_pdf && <Btn s="sm" onClick={downloadPDF} ch={<>{ic.download(C.wh)} Download PDF</>} />}
        {alignedImg && <Btn s="sm" v="out" onClick={downloadPage} ch={<>{ic.download(C.pri)} Page {viewPage}</>} />}
      </div>
    </div>}

    {/* Page navigation */}
    <PageNav />

    <div className="g1-m" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
      {/* Left: Original */}
      <div style={{ background: C.wh, borderRadius: 12, padding: 24, border: `1px solid ${C.bd}` }}>
        <h3 style={{ fontFamily: "'Outfit'", fontSize: 17, fontWeight: 700, marginBottom: 16 }}>
          {result ? "Original" : "Upload Document"}
        </h3>
        {!aDoc ? <UploadZone onFiles={async f => {
          const file = f[0];
          const d = await toBase64(file);
          setADoc({ name: file.name, type: file.type, dataUrl: d, file });
          setResult(null); setOrigImg(null); setAlignedImg(null);
        }} /> : !result ? <>
          {aDoc.type?.startsWith("image/")
            ? <img src={aDoc.dataUrl} alt="" style={{ maxWidth: "100%", maxHeight: 600, borderRadius: 8, border: `1px solid ${C.bd}` }} />
            : <div style={{ padding: 30, textAlign: "center", background: C.off, borderRadius: 8 }}><div style={{ fontSize: 36 }}>📄</div><div style={{ fontSize: 15, fontWeight: 600, marginTop: 8 }}>{aDoc.name}</div></div>}
          <button onClick={() => { setADoc(null); setResult(null); }} style={{ marginTop: 12, width: "100%", padding: 10, background: C.off, border: `1px solid ${C.bd}`, borderRadius: 8, fontSize: 14, color: C.txM, cursor: "pointer" }}>Change</button>
          <Btn sx={{ width: "100%", marginTop: 12 }} onClick={handleAlign}
            ch={aligning ? <><div style={{ width: 18, height: 18, border: "2px solid rgba(255,255,255,.3)", borderTopColor: C.wh, borderRadius: "50%", animation: "spin .7s linear infinite" }} /> Aligning...</> : <>{ic.align(C.wh)} Align Document</>} />
        </> : <>
          {aDoc.type?.startsWith("image/")
            ? <img src={aDoc.dataUrl} alt="" style={{ maxWidth: "100%", maxHeight: 600, borderRadius: 8, border: `1px solid ${C.bd}` }} />
            : origImg ? <img src={origImg} alt={`Original page ${viewPage}`} style={{ maxWidth: "100%", maxHeight: 600, borderRadius: 6, border: `1px solid ${C.bd}` }} />
            : <div style={{ textAlign: "center", padding: 40 }}><div style={{ fontSize: 36 }}>📄</div><div style={{ fontSize: 15, color: C.txM, marginTop: 8 }}>Page {viewPage}</div></div>}
          <button onClick={() => { setADoc(null); setResult(null); setOrigImg(null); setAlignedImg(null); }} style={{ marginTop: 12, width: "100%", padding: 10, background: C.off, border: `1px solid ${C.bd}`, borderRadius: 8, fontSize: 14, color: C.txM, cursor: "pointer" }}>New Document</button>
        </>}
      </div>

      {/* Right: Aligned */}
      <div style={{ background: C.wh, borderRadius: 12, padding: 24, border: `1px solid ${C.bd}` }}>
        <h3 style={{ fontFamily: "'Outfit'", fontSize: 17, fontWeight: 700, marginBottom: 16 }}>Aligned Result</h3>
        {result ? <>
          {imgLoading ? <div style={{ textAlign: "center", padding: 40 }}><div style={{ width: 32, height: 32, border: `3px solid ${C.bd}`, borderTopColor: C.pri, borderRadius: "50%", animation: "spin .7s linear infinite", margin: "0 auto" }} /></div> :
          alignedImg ? <img src={alignedImg} alt={`Aligned page ${viewPage}`} style={{ maxWidth: "100%", maxHeight: 600, borderRadius: 6, border: `1px solid ${C.bd}`, display: "block", margin: "0 auto" }} /> :
          <div style={{ textAlign: "center", padding: 40, color: C.txM }}>Page preview not available</div>}
        </> : <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 300 }}>
          {aligning ? <><div style={{ width: 44, height: 44, border: `3px solid ${C.bd}`, borderTopColor: C.pri, borderRadius: "50%", animation: "spin .7s linear infinite", marginBottom: 16 }} /><p style={{ color: C.txM, fontSize: 17 }}>Analyzing orientation...</p></> :
          <><div style={{ marginBottom: 16, transform: "scale(2.5)", opacity: 0.5 }}>{ic.align(C.txD)}</div><p style={{ color: C.txM, marginTop: 12, fontSize: 18 }}>Aligned document will appear here</p></>}
        </div>}
      </div>
    </div>
  </P>;
}

// ═══════════════════════════════════════
// BATCH TAB (must be outside DashboardPage to prevent state loss)
// ═══════════════════════════════════════
const SUPPORTED_EXT = [".pdf", ".png", ".jpg", ".jpeg", ".tif", ".tiff", ".bmp", ".webp"];
const isSupportedFile = (f) => SUPPORTED_EXT.some(e => f.name.toLowerCase().endsWith(e));
const fmtSize = (b) => b < 1024 ? b + " B" : b < 1048576 ? (b / 1024).toFixed(1) + " KB" : (b / 1048576).toFixed(1) + " MB";

function BatchTab({ notify }) {
  const user = getUser();
  const [files, setFiles] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [results, setResults] = useState([]);
  const [useAlign, setUseAlign] = useState(false);
  const [stopped, setStopped] = useState(false);
  const stopRef = useRef(false);
  const [expandedRow, setExpandedRow] = useState(-1);

  const eligible = files.filter(isSupportedFile);
  const totalSigs = results.reduce((sum, r) => sum + (r.signatures?.length || 0), 0);
  const doneCount = results.length;
  const errCount = results.filter(r => r.status === "error").length;
  const pct = eligible.length > 0 ? Math.round((doneCount / eligible.length) * 100) : 0;

  const handleFolderSelect = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.webkitdirectory = true;
    input.multiple = true;
    input.onchange = (e) => {
      if (e.target.files?.length > 0) {
        const all = Array.from(e.target.files);
        setFiles(all);
        setResults([]); setCurrentIdx(-1); setStopped(false); stopRef.current = false;
      }
    };
    document.body.appendChild(input);
    input.click();
    setTimeout(() => { try { document.body.removeChild(input); } catch(ex){} }, 10000);
  };

  const handleFileSelect = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = SUPPORTED_EXT.join(",");
    input.onchange = (e) => {
      if (e.target.files?.length > 0) {
        setFiles(Array.from(e.target.files));
        setResults([]); setCurrentIdx(-1); setStopped(false); stopRef.current = false;
      }
    };
    document.body.appendChild(input);
    input.click();
    setTimeout(() => { try { document.body.removeChild(input); } catch(ex){} }, 10000);
  };

  const startBatch = async () => {
    if (eligible.length === 0) return;
    setProcessing(true); setResults([]); setCurrentIdx(0); setStopped(false); stopRef.current = false;
    const newResults = [];

    for (let i = 0; i < eligible.length; i++) {
      if (stopRef.current) break;
      setCurrentIdx(i);
      const file = eligible[i];
      const formData = new FormData();
      formData.append("pdf", file);
      formData.append("filename", file.name);
      formData.append("model_type", "model2");
      formData.append("use_alignment", "true");
      if (user.companyId) formData.append("company_id", user.companyId);
      if (user.id) formData.append("user_id", user.id);

      try {
        const t0 = Date.now();
        const res = await fetch(`${API_BASE}/api/extract_signatures`, { method: "POST", body: formData });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const perf = data.performance || {};
        const elapsed = perf.elapsed_sec || parseFloat(((Date.now() - t0) / 1000).toFixed(1));
        const r = { filename: file.name, size: file.size, status: "success", page_count: data.page_count || 1, elapsed_sec: elapsed, cpu_percent: perf.system_cpu_percent || null, memory_mb: perf.process_memory_mb || null, memory_used_percent: perf.system_memory_used_percent || null, signatures: (data.signatures || []).map((s, j) => ({ sigNo: j + 1, page: s.page || 1, bbox: s.bbox || null, confidence: s.confidence || null, data: s.data || null })) };
        newResults.push(r);
      } catch (e) {
        newResults.push({ filename: file.name, size: file.size, status: "error", error: e.message, signatures: [] });
      }
      setResults([...newResults]);
    }

    setProcessing(false);
    setCurrentIdx(-1);
    if (stopRef.current) { setStopped(true); notify(`Batch stopped — ${newResults.length}/${eligible.length} processed`, "warning"); }
    else { notify(`Batch complete — ${newResults.length} documents, ${newResults.reduce((s, r) => s + (r.signatures?.length || 0), 0)} signatures found`, "success"); }
  };

  const stopBatch = () => { stopRef.current = true; };

  const exportJSON = () => {
    const data = results.map(r => ({ document: r.filename, pages: r.page_count, status: r.status, signatures: (r.signatures || []).map(s => ({ no: s.sigNo, page: s.page, ...(s.bbox ? { bbox: { x1: s.bbox[0], y1: s.bbox[1], x2: s.bbox[2], y2: s.bbox[3] } } : {}) })) }));
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = `batch_results_${Date.now()}.json`; a.click(); URL.revokeObjectURL(url);
  };

  const exportCSV = () => {
    let csv = "Document,Pages,Status,Signature_No,Page,X1,Y1,X2,Y2\n";
    results.forEach(r => {
      if (r.signatures?.length > 0) {
        r.signatures.forEach(s => {
          const bb = s.bbox || [0, 0, 0, 0];
          csv += `"${r.filename}",${r.page_count || 1},${r.status},${s.sigNo},${s.page},${bb[0]},${bb[1]},${bb[2]},${bb[3]}\n`;
        });
      } else {
        csv += `"${r.filename}",${r.page_count || 1},${r.status},,,,,\n`;
      }
    });
    const blob = new Blob([csv], { type: "text/csv" }); const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = `batch_results_${Date.now()}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  const clearAll = () => { setFiles([]); setResults([]); setCurrentIdx(-1); setStopped(false); stopRef.current = false; };

  return <P title="Batch Processing" sub="Process multiple documents at once — extract signatures from entire folders">
    {/* Top bar: file selection */}
    {files.length === 0 ? <div style={{ background: C.wh, borderRadius: 12, padding: 32, border: `1px solid ${C.bd}`, textAlign: "center" }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>📁</div>
      <h3 style={{ fontFamily: "'Outfit'", fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Select Documents</h3>
      <p style={{ fontSize: 14, color: C.txM, marginBottom: 24 }}>Choose a folder or select individual files to process</p>
      <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
        <Btn onClick={handleFolderSelect} ch={<>{ic.folder(C.wh)} Select Folder</>} />
        <Btn v="out" onClick={handleFileSelect} ch={<>{ic.upload(C.pri)} Select Files</>} />
      </div>
      <div style={{ marginTop: 20, display: "flex", justifyContent: "center", gap: 16, flexWrap: "wrap" }}>
        {SUPPORTED_EXT.map(e => <span key={e} style={{ fontSize: 12, color: C.txD, background: C.off, padding: "3px 10px", borderRadius: 6, border: `1px solid ${C.bdLt}` }}>{e.toUpperCase().slice(1)}</span>)}
      </div>
    </div> : <>
      {/* File info bar */}
      <div style={{ background: C.wh, borderRadius: 12, padding: "14px 20px", border: `1px solid ${C.bd}`, marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {ic.folder(C.pri)}
          <div>
            <span style={{ fontSize: 14, fontWeight: 600, color: C.tx }}>{eligible.length} eligible document{eligible.length !== 1 ? "s" : ""}</span>
            {files.length > eligible.length && <span style={{ fontSize: 12, color: C.txD, marginLeft: 8 }}>({files.length - eligible.length} skipped)</span>}
            <div style={{ fontSize: 12, color: C.txM }}>{fmtSize(eligible.reduce((s, f) => s + f.size, 0))} total</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {!processing && results.length === 0 && <>
            <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: C.txM }}>{ic.align(C.pri)} Auto-align on</span>
            <Btn onClick={startBatch} ch={<>{ic.batch(C.wh)} Start Batch</>} />
          </>}
          {processing && <Btn v="out" onClick={stopBatch} ch={<>{ic.x()} Stop</>} />}
          {!processing && results.length > 0 && <>
            <Btn s="sm" onClick={exportJSON} ch={<>{ic.download(C.wh)} JSON</>} />
            <Btn s="sm" v="out" onClick={exportCSV} ch={<>{ic.download(C.pri)} CSV</>} />
          </>}
          <button onClick={clearAll} style={{ background: "none", border: "none", color: C.txM, fontSize: 13, cursor: "pointer" }}>{ic.x()} Clear</button>
        </div>
      </div>

      {/* Progress bar */}
      {(processing || results.length > 0) && <div style={{ background: C.wh, borderRadius: 12, padding: "16px 20px", border: `1px solid ${C.bd}`, marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: C.tx }}>{processing ? `Processing ${currentIdx + 1} of ${eligible.length}...` : stopped ? "Stopped" : "Completed"}</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: C.pri }}>{pct}%</span>
        </div>
        <div style={{ height: 8, background: C.bdLt, borderRadius: 4, overflow: "hidden" }}>
          <div style={{ width: `${pct}%`, height: "100%", background: processing ? C.pri : errCount > 0 ? C.warn : C.ok, borderRadius: 4, transition: "width .3s ease" }} />
        </div>
        <div style={{ display: "flex", gap: 16, marginTop: 10, fontSize: 12, color: C.txM }}>
          <span style={{ color: C.ok, fontWeight: 600 }}>✓ {doneCount - errCount} success</span>
          {errCount > 0 && <span style={{ color: C.err, fontWeight: 600 }}>✗ {errCount} failed</span>}
          <span>🖊 {totalSigs} signatures found</span>
          {processing && currentIdx >= 0 && <span style={{ color: C.pri }}>→ {eligible[currentIdx]?.name}</span>}
        </div>
      </div>}

      {/* Results table */}
      {results.length > 0 && <div style={{ background: C.wh, borderRadius: 12, border: `1px solid ${C.bd}`, overflow: "hidden" }}>
        <div style={{ padding: "12px 18px", borderBottom: `1px solid ${C.bd}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ fontFamily: "'Outfit'", fontSize: 15, fontWeight: 700 }}>Results</h3>
          <span style={{ fontSize: 12, color: C.txM }}>{results.length} / {eligible.length}</span>
        </div>
        <div style={{ overflowX: "auto", maxHeight: 400, overflowY: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 600 }}>
            <thead><tr style={{ borderBottom: `1px solid ${C.bd}`, background: C.off, position: "sticky", top: 0 }}>
              {[...["#", "Document", "Size", "Pages", "Signatures", "Status"], ...(isEnterprise() ? ["Time", "CPU", "RAM"] : [])].map(h => <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, fontWeight: 600, color: C.txD, textTransform: "uppercase" }}>{h}</th>)}
            </tr></thead>
            <tbody>{results.map((r, i) => <Fragment key={i}>
              <tr onClick={() => setExpandedRow(expandedRow === i ? -1 : i)} style={{ borderBottom: expandedRow === i ? "none" : `1px solid ${C.bdLt}`, cursor: r.signatures?.length > 0 ? "pointer" : "default", background: expandedRow === i ? C.priPl : "transparent", transition: "background .15s" }}>
              <td style={{ padding: "8px 12px", fontSize: 13, color: C.txD }}>{i + 1}</td>
              <td style={{ padding: "8px 12px", fontSize: 13, fontWeight: 500, color: C.tx, maxWidth: 250, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.filename}</td>
              <td style={{ padding: "8px 12px", fontSize: 12, color: C.txM }}>{fmtSize(r.size)}</td>
              <td style={{ padding: "8px 12px", fontSize: 13 }}>{r.page_count || "—"}</td>
              <td style={{ padding: "8px 12px" }}>
                {r.status === "error" ? <span style={{ fontSize: 12, color: C.err }}>—</span> :
                <span style={{ fontSize: 13, fontWeight: 600, color: r.signatures?.length > 0 ? C.ok : C.txD }}>{r.signatures?.length || 0} {r.signatures?.length > 0 ? "▾" : ""}</span>}
              </td>
              <td style={{ padding: "8px 12px" }}>
                <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 6, color: r.status === "success" ? C.ok : C.err, background: r.status === "success" ? `${C.ok}10` : `${C.err}10` }}>
                  {r.status === "success" ? "✓ Done" : "✗ Error"}
                </span>
              </td>
              {isEnterprise() && <td style={{ padding: "8px 12px", fontSize: 12, fontFamily: "'JetBrains Mono'", color: C.txM }}>{r.elapsed_sec ? `${r.elapsed_sec}s` : "—"}</td>}
              {isEnterprise() && <td style={{ padding: "8px 12px", fontSize: 12, fontFamily: "'JetBrains Mono'", color: r.cpu_percent > 80 ? C.err : r.cpu_percent > 50 ? C.warn : C.txM }}>{r.cpu_percent ? `${r.cpu_percent}%` : "—"}</td>}
              {isEnterprise() && <td style={{ padding: "8px 12px", fontSize: 12, fontFamily: "'JetBrains Mono'", color: r.memory_used_percent > 85 ? C.err : r.memory_used_percent > 70 ? C.warn : C.txM }}>{r.memory_mb ? `${r.memory_mb}MB` : "—"}</td>}
            </tr>
            {expandedRow === i && r.signatures?.length > 0 && <tr><td colSpan={isEnterprise() ? 9 : 6} style={{ padding: "12px 16px", background: `${C.pri}04`, borderBottom: `1px solid ${C.bdLt}` }}>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {r.signatures.map((s, j) => <div key={j} style={{ background: C.wh, borderRadius: 8, padding: 8, border: `1px solid ${C.bd}`, textAlign: "center" }}>
                  {s.data ? <img src={s.data} alt={`Sig ${s.sigNo}`} style={{ width: 120, height: 60, objectFit: "contain", borderRadius: 4, background: C.off }} /> : <div style={{ width: 120, height: 60, borderRadius: 4, background: C.off, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: C.txD }}>No preview</div>}
                  <div style={{ fontSize: 12, fontWeight: 600, color: C.pri, marginTop: 4 }}>#{s.sigNo}</div>
                  <div style={{ fontSize: 11, color: C.txM }}>Page {s.page}</div>
                </div>)}
              </div>
            </td></tr>}
            </Fragment>)}</tbody>
          </table>
        </div>
      </div>}

      {/* File list (before processing) */}
      {results.length === 0 && !processing && <div style={{ background: C.wh, borderRadius: 12, border: `1px solid ${C.bd}`, overflow: "hidden" }}>
        <div style={{ padding: "12px 18px", borderBottom: `1px solid ${C.bd}` }}>
          <h3 style={{ fontFamily: "'Outfit'", fontSize: 15, fontWeight: 700 }}>Documents to Process</h3>
        </div>
        <div style={{ maxHeight: 350, overflowY: "auto" }}>
          {eligible.map((f, i) => <div key={i} style={{ padding: "8px 18px", borderBottom: `1px solid ${C.bdLt}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {ic.file(C.pri)}
              <span style={{ fontSize: 13, color: C.tx, maxWidth: 350, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
            </div>
            <span style={{ fontSize: 12, color: C.txD }}>{fmtSize(f.size)}</span>
          </div>)}
        </div>
      </div>}
    </>}
  </P>;
}

// ═══════════════════════════════════════
// CLEAN TAB (reusable for Basic Clean + Stamp Clean)
// `ver` prop: version badge(s) shown next to the page title
// ═══════════════════════════════════════
function CleanTab({ notify, workspaceSigs, selectedSigNos, method = "model_s", title = "Signature Cleaning", subtitle = "Clean signatures using AI" }) {
  const [uploadedSigs, setUploadedSigs] = useState([]);
  const [cleaning, setCleaning] = useState(false);
  const [results, setResults] = useState({});
  const removeText = true;
  const cleanLines = false; // primitive border-line baking disabled (was cutting long signature strokes)
  const [currentIdx, setCurrentIdx] = useState(-1);

  const allWsSigs = workspaceSigs && workspaceSigs.length > 0 ? workspaceSigs : [];
  const filteredWsSigs = selectedSigNos && selectedSigNos.length > 0
    ? allWsSigs.filter(s => selectedSigNos.includes(s.sigNo))
    : allWsSigs;
  const sigs = filteredWsSigs.length > 0 ? filteredWsSigs : uploadedSigs;
  const doneCount = Object.keys(results).length;

  const handleUpload = async (files) => {
    const newSigs = [];
    for (let i = 0; i < files.length; i++) {
      const dataUrl = await toBase64(files[i]);
      newSigs.push({ sigNo: uploadedSigs.length + i + 1, data: dataUrl, imageBase64: dataUrl, name: files[i].name, page: 1 });
    }
    setUploadedSigs(prev => [...prev, ...newSigs]);
    setResults({});
  };

  const cleanOne = async (sig) => {
    const sigData = sig.data || sig.imageBase64;
    if (!sigData) return null;
    try {
      const res = await fetch(`${API_BASE}/api/clean_signature`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signature_base64: sigData, remove_text: removeText, clean_lines: cleanLines, method })
      });
      if (!res.ok) throw new Error("Failed");
      return await res.json();
    } catch (e) { return null; }
  };

  const cleanAll = async () => {
    if (sigs.length === 0) return;
    setCleaning(true); setResults({});
    const newResults = {};
    for (let i = 0; i < sigs.length; i++) {
      setCurrentIdx(i);
      const result = await cleanOne(sigs[i]);
      if (result) newResults[sigs[i].sigNo] = result;
      setResults({ ...newResults });
    }
    setCurrentIdx(-1); setCleaning(false);
    const successCount = Object.keys(newResults).length;
    notify(`${successCount}/${sigs.length} signature(s) cleaned`, successCount > 0 ? "success" : "error");
  };

  const cleanSingle = async (sig) => {
    setCleaning(true); setCurrentIdx(sigs.indexOf(sig));
    const result = await cleanOne(sig);
    if (result) { setResults(prev => ({ ...prev, [sig.sigNo]: result })); notify("Signature cleaned", "success"); }
    else { notify("Cleaning failed", "error"); }
    setCurrentIdx(-1); setCleaning(false);
  };

  const downloadCleaned = (sig) => {
    const r = results[sig.sigNo];
    if (!r?.cleaned_signature) return;
    const a = document.createElement("a"); a.href = r.cleaned_signature; a.download = `signature_${sig.sigNo}_cleaned.png`; a.click();
  };

  const clearAll = () => { setUploadedSigs([]); setResults({}); };

  return <P title={title} sub={subtitle}>
    {/* Options bar */}
    <div style={{ background: C.wh, borderRadius: 12, padding: "14px 20px", border: `1px solid ${C.bd}`, marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        {allWsSigs.length > 0 && <span style={{ fontSize: 13, color: C.txS }}>{sigs.length} of {allWsSigs.length} signature{allWsSigs.length > 1 ? "s" : ""}{selectedSigNos?.length > 0 ? " selected" : ""}</span>}
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {sigs.length > 0 && !cleaning && <Btn onClick={cleanAll} ch={<>{ic.clean(C.wh)} Clean All ({sigs.length})</>} />}
        {doneCount > 0 && <span style={{ fontSize: 13, color: C.ok, fontWeight: 600 }}>✓ {doneCount} done</span>}
        {uploadedSigs.length > 0 && <button onClick={clearAll} style={{ background: "none", border: "none", color: C.txM, fontSize: 13, cursor: "pointer" }}>Clear</button>}
      </div>
    </div>

    {/* No signatures */}
    {sigs.length === 0 && <div style={{ background: C.wh, borderRadius: 12, padding: 32, border: `1px solid ${C.bd}`, marginBottom: 16, textAlign: "center" }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>
      <h3 style={{ fontFamily: "'Outfit'", fontSize: 17, fontWeight: 700, color: C.tx, marginBottom: 8 }}>No Signatures to Clean</h3>
      <p style={{ fontSize: 14, color: C.txM, lineHeight: 1.6, maxWidth: 400, margin: "0 auto" }}>Extract signatures from a document first in the Extraction tab, then come back here to clean them.</p>
    </div>}

    {/* Progress bar */}
    {cleaning && <div style={{ background: C.wh, borderRadius: 12, padding: "12px 20px", border: `1px solid ${C.bd}`, marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <span style={{ fontSize: 14, color: C.tx }}>Cleaning {currentIdx + 1} of {sigs.length}...</span>
        <span style={{ fontSize: 14, fontWeight: 700, color: C.pri }}>{Math.round((doneCount / sigs.length) * 100)}%</span>
      </div>
      <div style={{ height: 6, background: C.bdLt, borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: `${(doneCount / sigs.length) * 100}%`, height: "100%", background: C.pri, borderRadius: 3, transition: "width .3s" }} />
      </div>
    </div>}

    {/* Signature cards grid */}
    {sigs.length > 0 && <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(440px, 1fr))", gap: 16 }}>
      {sigs.map(sig => {
        const r = results[sig.sigNo];
        const isCurrent = cleaning && sigs[currentIdx]?.sigNo === sig.sigNo;
        return <div key={sig.sigNo} style={{ background: C.wh, borderRadius: 12, border: `1px solid ${isCurrent ? C.pri : C.bd}`, overflow: "hidden" }}>
          <div style={{ padding: "10px 16px", borderBottom: `1px solid ${C.bd}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: r ? `${C.ok}06` : isCurrent ? C.priPl : "transparent" }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: C.tx }}>Signature #{sig.sigNo}</span>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              {r && <button onClick={() => downloadCleaned(sig)} style={{ background: "none", border: "none", color: C.pri, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{ic.download(C.pri)} Save</button>}
              {!r && !cleaning && <button onClick={() => cleanSingle(sig)} style={{ padding: "5px 14px", borderRadius: 6, border: `1px solid ${C.pri}`, background: C.priPl, color: C.pri, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Clean</button>}
              {isCurrent && <div style={{ width: 16, height: 16, border: `2px solid ${C.bd}`, borderTopColor: C.pri, borderRadius: "50%", animation: "spin .7s linear infinite" }} />}
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: r ? "1fr 1fr" : "1fr", gap: 0 }}>
            <div style={{ padding: 18, textAlign: "center", borderRight: r ? `1px solid ${C.bd}` : "none" }}>
              <div style={{ fontSize: 13, color: C.txD, marginBottom: 6, fontWeight: 600 }}>{r ? "BEFORE" : "ORIGINAL"}</div>
              <img src={sig.data || sig.imageBase64} alt={`Sig ${sig.sigNo}`} style={{ maxWidth: "100%", maxHeight: 240, objectFit: "contain", borderRadius: 6, background: C.off }} />
            </div>
            {r && <div style={{ padding: 18, textAlign: "center" }}>
              <div style={{ fontSize: 13, color: C.ok, marginBottom: 6, fontWeight: 600 }}>AFTER</div>
              <img src={r.cleaned_signature} alt={`Cleaned ${sig.sigNo}`} style={{ maxWidth: "100%", maxHeight: 240, objectFit: "contain", borderRadius: 6, background: C.off }} />
            </div>}
          </div>
          {r && <div style={{ padding: "6px 14px", borderTop: `1px solid ${C.bdLt}`, display: "flex", gap: 4, flexWrap: "wrap" }}>
            {r.steps_applied?.map((s, i) => <span key={i} style={{ fontSize: 11, color: C.txD, background: C.off, padding: "2px 6px", borderRadius: 3 }}>{({"text_removed":"Text","lines_cleaned":"Lines","model_g":"AI Clean","model_s":"AI Segment","cyclegan_model_g":"AI Clean","segformer_model_s":"AI Segment"})[s] || s}</span>)}
          </div>}
        </div>;
      })}
    </div>}
  </P>;
}

// ═══════════════════════════════════════
// SIGNATURE ABSENCE TAB
// ═══════════════════════════════════════
function SignatureAbsenceTab({ notify }) {
  const user = getUser();
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);
  const [selectedPage, setSelectedPage] = useState(1);

  const loadFile = (f) => {
    setFile(f);
    setResult(null);
    setSelectedPage(1);
    if (f.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = () => setFilePreview(reader.result);
      reader.readAsDataURL(f);
    } else {
      setFilePreview(null);
    }
  };

  const handleCheck = async () => {
    if (!file) { notify("Upload a document first", "warning"); return; }
    setChecking(true); setResult(null);
    const formData = new FormData();
    formData.append("pdf", file);
    formData.append("filename", file.name);
    formData.append("model_type", "model2");
    formData.append("use_alignment", "true");
    if (user.companyId) formData.append("company_id", user.companyId);
    if (user.id) formData.append("user_id", user.id);
    try {
      const res = await fetch(`${API_BASE}/api/extract_signatures`, { method: "POST", body: formData });
      if (!res.ok) throw new Error("Detection failed");
      const data = await res.json();
      const sigCount = data.signatures?.length || 0;
      setResult({ found: sigCount > 0, count: sigCount, pages: data.page_count || 1, signatures: data.signatures || [], docBase: file.name.replace(/\.[^.]+$/, "") });
    } catch (e) { notify("Check failed: " + e.message, "error"); }
    setChecking(false);
  };

  // For PDF page preview after check
  const docBase = result?.docBase || file?.name?.replace(/\.[^.]+$/, "") || "";
  const pagePreviewUrl = docBase ? `${API_BASE}/static/output/${encodeURIComponent(docBase)}/pages/page_1.jpg` : null;
  const docObj = file ? { name: file.name, type: file.type, pageCount: file.type.startsWith("image/") ? 1 : (result?.pages || 0), dataUrl: filePreview } : null;

  return <P title="Signature Absence Check" sub="Detect whether a document contains wet signatures">
    <div className="g1-m" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
      {/* Left: Upload + Document Preview */}
      <div>
        <div style={{ background: C.wh, borderRadius: 12, padding: 24, border: `1px solid ${C.bd}` }}>
          <h3 style={{ fontFamily: "'Outfit'", fontSize: 17, fontWeight: 700, marginBottom: 16 }}>Upload Document</h3>
          {file ? <>
            {/* Document preview */}
            <div style={{ marginBottom: 16 }}>
              <DocPreview doc={docObj} signatures={[]} selectedPage={selectedPage} onPageChange={setSelectedPage} aligned={false} />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn sx={{ flex: 1 }} onClick={handleCheck}
                ch={checking ? <><div style={{ width: 18, height: 18, border: "2px solid rgba(255,255,255,.3)", borderTopColor: C.wh, borderRadius: "50%", animation: "spin .7s linear infinite" }} /> Checking...</> : <>{ic.detect(C.wh)} Check Document</>} />
              <button onClick={() => { setFile(null); setFilePreview(null); setResult(null); setSelectedPage(1); }} style={{ padding: "0 14px", borderRadius: 8, border: `1px solid ${C.bd}`, background: C.wh, fontSize: 13, color: C.txM, cursor: "pointer" }}>Change</button>
            </div>
          </> : <UploadZone onFiles={f => loadFile(f[0])} />}
        </div>
      </div>

      {/* Right: Result */}
      <div style={{ background: C.wh, borderRadius: 12, padding: 24, border: `1px solid ${result ? (result.found ? C.ok : C.err) + "40" : C.bd}`, borderWidth: result ? 2 : 1 }}>
        {result ? <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div style={{ fontSize: 72, marginBottom: 16 }}>{result.found ? "✅" : "❌"}</div>
          <div style={{ fontFamily: "'Outfit'", fontSize: 30, fontWeight: 800, color: result.found ? C.ok : C.err, marginBottom: 8 }}>
            {result.found ? "Signatures Found" : "No Signatures"}
          </div>
          <div style={{ fontSize: 17, color: C.txM, marginBottom: 20 }}>
            {result.found ? `${result.count} signature(s) detected across ${result.pages} page(s)` : `Scanned ${result.pages} page(s) — no wet signatures detected`}
          </div>
          {result.found && result.signatures.length > 0 && <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 12, marginTop: 16 }}>
            {result.signatures.map((s, i) => <div key={i} style={{ background: C.off, borderRadius: 10, padding: 10, textAlign: "center", border: `1px solid ${C.bd}` }}>
              {s.data && <img src={s.data} alt="" style={{ width: "100%", height: 80, objectFit: "contain", borderRadius: 6, background: C.wh, marginBottom: 6 }} />}
              <div style={{ fontSize: 14, fontWeight: 600, color: C.pri }}>#{s.id || i + 1}</div>
              <div style={{ fontSize: 13, color: C.txM }}>Page {s.page || 1}</div>
            </div>)}
          </div>}
        </div> : <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 400 }}>
          {checking ? <><div style={{ width: 44, height: 44, border: `3px solid ${C.bd}`, borderTopColor: C.pri, borderRadius: "50%", animation: "spin .7s linear infinite", marginBottom: 16 }} /><p style={{ color: C.txM, fontSize: 17 }}>Scanning document...</p></> :
          <><div style={{ marginBottom: 16, transform: "scale(2.5)", opacity: 0.5 }}>{ic.detect(C.txD)}</div><p style={{ color: C.txM, marginTop: 12, fontSize: 18 }}>Upload a document to check for signatures</p></>}
        </div>}
      </div>
    </div>
  </P>;
}

// ═══════════════════════════════════════
// COMPARE TAB (must be outside DashboardPage to prevent state loss)
// ═══════════════════════════════════════
function CompareTab({ notify, workspaceSigs, workspaceStamps = [], parentCleanCache = {} }) {
  const user = getUser();
  const [mode, setMode] = useState(0); // 0=vs DB, 1=within doc, 2=two docs, 3=upload
  const [comparing, setComparing] = useState(false);
  const [result, setResult] = useState(null);
  const [customerId, setCustomerId] = useState("");
  const [selA, setSelA] = useState(0);
  const [selB, setSelB] = useState(-1);
  const [docBFile, setDocBFile] = useState(null);
  const [docBSigs, setDocBSigs] = useState([]);
  const [detectingB, setDetectingB] = useState(false);
  const [uploadSigB, setUploadSigB] = useState(null);
  const [cleanedCache, setCleanedCache] = useState({}); // local additions (db results, docB, uploads)
  const [showCleaned, setShowCleaned] = useState(true); // VIEW toggle: original vs cleaned (display only)
  const [uploadCleanMethod, setUploadCleanMethod] = useState("model_s"); // cleaning applied ONLY to a manually uploaded signature
  const [busyMsg, setBusyMsg] = useState("");   // live status while cleaning/comparing

  // Merge parent cache + local cache for display
  const mergedCache = { ...parentCleanCache, ...cleanedCache };

  const sigs = workspaceSigs || [];
  const sigA = sigs[selA];

  // ── Smart cleaning: pick method per signature from stamp overlap ──
  const overlapsStamp = (sig) => {
    if (!sig?.bbox || !workspaceStamps || workspaceStamps.length === 0) return false;
    const [ax1, ay1, ax2, ay2] = sig.bbox;
    return workspaceStamps.some(st => {
      if (st.page && sig.page && st.page !== sig.page) return false;
      const b = st.bbox; if (!b) return false;
      const [bx1, by1, bx2, by2] = b;
      return ax1 < bx2 && ax2 > bx1 && ay1 < by2 && ay2 > by1;
    });
  };
  // "model_g" = stamp cleaning (signature touches a stamp), else "model_s" = basic
  const smartMethod = (sig) => sig?.cleaning_method ? (sig.cleaning_method === "stamp" ? "model_g" : "model_s") : (overlapsStamp(sig) ? "model_g" : "model_s");

  // Clean a single signature image via API with an explicit method
  const cleanSigImage = async (sigData, method, opts = {}) => {
    if (!sigData) return null;
    try {
      const res = await fetch(`${API_BASE}/api/clean_signature`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signature_base64: sigData, clean_lines: false, method: method || "model_s" })
      });
      if (!res.ok) {
        // Surface the failure — a silent fallback to the original image looks like
        // "cleaning does nothing" (e.g. 503 when the stamp model isn't loaded).
        let detail = `HTTP ${res.status}`;
        try { const e = await res.json(); if (e.detail) detail = e.detail; } catch {}
        if (opts.loud) notify(`Cleaning failed (${method}): ${detail}`, "error");
        console.error("clean_signature failed:", method, detail);
        return null;
      }
      const data = await res.json();
      return data.cleaned_signature || null;
    } catch (e) {
      if (opts.loud) notify(`Cleaning failed (${method}): ${e.message}`, "error");
      return null;
    }
  };

  // Ensure a signature is cleaned (smart method) and cached; return the cleaned base64
  const ensureCleaned = async (sig, key) => {
    const orig = sig?.data || sig?.imageBase64;
    if (!orig) return orig;
    if (mergedCache[key]) return mergedCache[key];
    const cleaned = await cleanSigImage(orig, smartMethod(sig));
    if (cleaned) { setCleanedCache(prev => ({ ...prev, [key]: cleaned })); return cleaned; }
    return orig;
  };

  // Display image: cleaned only when the VIEW toggle is on AND a cleaned version exists
  const getSigImg = (sig, key) => {
    if (!sig) return null;
    if (showCleaned && mergedCache[key]) return mergedCache[key];
    return sig.data || sig.imageBase64;
  };

  // Which cleaning method to use for a given display key: uploaded sig → user-chosen; others → smart
  const displayMethod = (key, sig) => key === "upload_B" ? (uploadCleanMethod === "none" ? null : uploadCleanMethod) : smartMethod(sig);

  // Clean signatures in background for DISPLAY
  const cleanForDisplay = (items) => {
    (async () => {
      const newCache = { ...cleanedCache };
      let changed = false;
      for (const { key, sig } of items) {
        const data = sig?.data || sig?.imageBase64;
        const m = displayMethod(key, sig);
        if (m && !mergedCache[key] && !newCache[key] && data) {
          const cleaned = await cleanSigImage(data, m);
          if (cleaned) { newCache[key] = cleaned; changed = true; }
        }
      }
      if (changed) setCleanedCache(newCache);
    })();
  };

  // Auto-clean sigs for display as soon as they're available
  useEffect(() => {
    const toClean = [];
    sigs.forEach(s => {
      const key = `ws_${s.sigNo}`;
      if (!mergedCache[key] && (s.data || s.imageBase64)) toClean.push({ key, sig: s });
    });
    docBSigs.forEach(s => {
      const key = `docB_${s.sigNo}`;
      if (!mergedCache[key] && (s.data || s.imageBase64)) toClean.push({ key, sig: s });
    });
    if (uploadSigB && !mergedCache["upload_B"]) toClean.push({ key: "upload_B", sig: uploadSigB });
    if (toClean.length > 0) cleanForDisplay(toClean);
  }, [sigs, docBSigs, uploadSigB, workspaceStamps, uploadCleanMethod]);

  // When the uploaded-signature cleaning method changes, drop its cached version so it re-cleans
  useEffect(() => {
    setCleanedCache(prev => { if (!prev["upload_B"]) return prev; const c = { ...prev }; delete c["upload_B"]; return c; });
  }, [uploadCleanMethod]);

  // Upload + detect signatures from Document B
  const handleUploadDocB = async (files) => {
    const file = files[0];
    setDocBFile(file);
    setDocBSigs([]);
    setResult(null);
    setDetectingB(true);
    const formData = new FormData();
    formData.append("pdf", file);
    formData.append("filename", file.name);
    formData.append("model_type", "model2");
    formData.append("use_alignment", "true");
    if (user.companyId) formData.append("company_id", user.companyId);
    if (user.id) formData.append("user_id", user.id);
    try {
      const res = await fetch(`${API_BASE}/api/extract_signatures`, { method: "POST", body: formData });
      if (!res.ok) throw new Error("Detection failed");
      const data = await res.json();
      const bSigs = (data.signatures || []).map((s, i) => ({ sigNo: i + 1, data: s.data, imageBase64: s.data, page: s.page, confidence: s.confidence, bbox: s.bbox || null, cleaning_method: s.cleaning_method || null }));
      setDocBSigs(bSigs);
      if (bSigs.length === 0) notify("No signatures found in Document B", "warning");
      else notify(`${bSigs.length} signature(s) detected in Document B`, "success");
    } catch (e) { notify("Detection failed: " + e.message, "error"); }
    setDetectingB(false);
  };

  // Mode 0: vs Database
  const compareVsDb = async () => {
    if (!sigA) { notify("Select a signature first", "warning"); return; }
    if (!customerId.trim()) { notify("Enter Customer ID", "warning"); return; }
    setComparing(true); setResult(null);
    const a = await ensureCleaned(sigA, `ws_${sigA.sigNo}`);
    const form = new FormData();
    form.append("customer_id", customerId);
    form.append("detected_signature", a || sigA.data || sigA.imageBase64);
    form.append("threshold", "0.0");
    form.append("cleaning", "none");
    try {
      const res = await fetch(`${API_BASE}/api/signature_comparison`, { method: "POST", body: form });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      setResult({ type: "db", data });
      // Clean for display in background (non-blocking)
      const toClean = [{ key: `ws_${sigA.sigNo}`, sig: sigA }];
      if (data.matches?.length > 0) {
        data.matches.forEach(m => {
          const img = m.signature_base64?.startsWith("data:") ? m.signature_base64 : m.signature_base64 ? `data:image/png;base64,${m.signature_base64}` : null;
          if (img) toClean.push({ key: `db_${m.id}`, sig: { data: img } });
        });
      }
      cleanForDisplay(toClean);
    } catch (e) { notify("Comparison failed: " + e.message, "error"); }
    setBusyMsg(""); setComparing(false);
  };

  // Mode 1: Within Document — compare 2 selected workspace sigs
  const compareWithin = async () => {
    if (sigs.length < 2) { notify("Need at least 2 signatures", "warning"); return; }
    if (selB < 0 || selB === selA) { notify("Select a second signature", "warning"); return; }
    const s1 = sigs[selA], s2 = sigs[selB];
    setComparing(true); setResult(null);
    setBusyMsg("Cleaning both signatures…");
    const [c1, c2] = await Promise.all([ensureCleaned(s1, `ws_${s1.sigNo}`), ensureCleaned(s2, `ws_${s2.sigNo}`)]);
    const form = new FormData();
    form.append("signature1_base64", c1 || s1.data || s1.imageBase64);
    form.append("signature2_base64", c2 || s2.data || s2.imageBase64);
    form.append("cleaning", "none");
    try {
      const res = await fetch(`${API_BASE}/api/compare_two_signatures`, { method: "POST", body: form });
      if (!res.ok) throw new Error("Failed");
      setResult({ type: "two", data: await res.json(), sigA: s1, sigB: s2 });
      cleanForDisplay([
        { key: `ws_${s1.sigNo}`, sig: s1 },
        { key: `ws_${s2.sigNo}`, sig: s2 }
      ]);
    } catch (e) { notify("Comparison failed: " + e.message, "error"); }
    setBusyMsg(""); setComparing(false);
  };

  // Mode 2: Two Documents — compare sigA from doc A vs ALL sigs in doc B
  const compareTwoDocs = async () => {
    if (!sigA) { notify("Select a signature from Document A", "warning"); return; }
    if (docBSigs.length === 0) { notify("Upload and detect Document B first", "warning"); return; }
    setComparing(true); setResult(null);
    setBusyMsg("Cleaning Document A signature…");
    const aClean = await ensureCleaned(sigA, `ws_${sigA.sigNo}`);
    setBusyMsg(`Cleaning ${docBSigs.length} signature(s) from Document B…`);
    const bClean = await Promise.all(docBSigs.map(s => ensureCleaned(s, `docB_${s.sigNo}`)));
    setBusyMsg("Comparing…");
    try {
      const res = await fetch(`${API_BASE}/api/compare_matrix`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          signatures_a: [aClean || sigA.data || sigA.imageBase64],
          signatures_b: bClean.map((c, j) => c || docBSigs[j].data || docBSigs[j].imageBase64),
          cleaning: "none"
        })
      });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      const matches = (data.matrix[0] || []).map((cell, j) => ({
        ...cell,
        sigBNo: docBSigs[j].sigNo,
        sigBImg: docBSigs[j].data || docBSigs[j].imageBase64
      }));
      matches.sort((a, b) => (b.similarity_raw || b.similarity || 0) - (a.similarity_raw || a.similarity || 0));
      setResult({ type: "twodoc", matches, sigA });
      notify(`Compared against ${matches.length} signature(s) from Document B`, "success");
    } catch (e) { notify("Comparison failed: " + e.message, "error"); }
    cleanForDisplay([
      { key: `ws_${sigA.sigNo}`, sig: sigA },
      ...docBSigs.map(s => ({ key: `docB_${s.sigNo}`, sig: s }))
    ]);
    setBusyMsg("");
    setComparing(false);
  };

  const handleCompare = () => { if (mode === 0) compareVsDb(); else if (mode === 1) compareWithin(); else if (mode === 2) compareTwoDocs(); else compareUploaded(); };

  // Mode 3: Compare two uploaded signatures
  const compareUploaded = async () => {
    const sigA = sigs[selA];
    if (!sigA) { notify("Select a document signature first", "warning"); return; }
    if (!uploadSigB) { notify("Upload a signature to compare against", "warning"); return; }
    setComparing(true); setResult(null);
    setBusyMsg("Cleaning document signature…");
    const c1 = await ensureCleaned(sigA, `ws_${sigA.sigNo}`); // workspace sig: automatic
    let c2 = uploadSigB.data || uploadSigB.imageBase64;
    if (uploadCleanMethod !== "none") { // uploaded sig: user-selected cleaning
      setBusyMsg(uploadCleanMethod === "model_g"
        ? "Cleaning uploaded signature with the stamp model — this can take a while…"
        : "Cleaning uploaded signature (basic)…");
      const cleaned = await cleanSigImage(uploadSigB.data || uploadSigB.imageBase64, uploadCleanMethod, { loud: true });
      if (!cleaned) { setBusyMsg(""); setComparing(false); return; }  // don't silently compare the raw image
      c2 = cleaned;
      setCleanedCache(prev => ({ ...prev, upload_B: cleaned }));      // reflect it in the preview
    }
    setBusyMsg("Comparing signatures…");
    const form = new FormData();
    form.append("signature1_base64", c1 || sigA.data || sigA.imageBase64);
    form.append("signature2_base64", c2);
    form.append("cleaning", "none");
    try {
      const res = await fetch(`${API_BASE}/api/compare_two_signatures`, { method: "POST", body: form });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      setResult({ type: "two", data, sigA, sigB: uploadSigB });
      cleanForDisplay([
        { key: `ws_${sigA.sigNo}`, sig: sigA },
        { key: "upload_B", sig: uploadSigB }
      ]);
    } catch (e) { notify("Comparison failed: " + e.message, "error"); }
    setBusyMsg("");
    setComparing(false);
  };

  const handleSigUpload = async (files, which) => {
    const file = files[0];
    const dataUrl = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(file);
    });
    const sig = { data: dataUrl, imageBase64: dataUrl, sigNo: "U", page: 1, name: file.name };
    setUploadSigB(sig);
    setResult(null);
  };

  const confColor = (v) => v >= 0.85 ? C.ok : v >= 0.5 ? C.warn : C.err;

  // Signature thumbnail selector
  const SigPicker = ({ items, selected, onSelect, label, keyPrefix = "ws" }) => <div style={{ marginBottom: 14 }}>
    <div style={{ fontSize: 14, fontWeight: 600, color: C.txS, marginBottom: 8 }}>{label}<span style={{ fontSize: 11, color: C.ok, marginLeft: 8 }}>🧹 otomatik</span></div>
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {items.map((s, i) => <div key={s.sigNo} onClick={() => onSelect(i)} style={{ padding: 4, borderRadius: 8, border: `2px solid ${selected === i ? C.pri : C.bd}`, cursor: "pointer", background: selected === i ? C.priPl : C.wh, transition: "all .15s" }}>
        <img src={getSigImg(s, `${keyPrefix}_${s.sigNo}`)} alt={`#${s.sigNo}`} style={{ width: 150, height: 85, objectFit: "contain", borderRadius: 6, background: C.off }} />
        <div style={{ fontSize: 13, textAlign: "center", color: selected === i ? C.pri : C.txM, fontWeight: 600 }}>#{s.sigNo}</div>
      </div>)}
    </div>
  </div>;

  return <P title="Signature Comparison" sub="Compare signatures across documents or against database">
    {/* Mode selector */}
    <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
      {["vs Database", "Within Document", "Two Documents", "Upload & Compare"].map((m, i) => <button key={i} onClick={() => { setMode(i); setResult(null); setSelB(-1); }} style={{ padding: "8px 18px", borderRadius: 8, border: `1px solid ${mode === i ? C.pri : C.bd}`, background: mode === i ? C.priPl : C.wh, color: mode === i ? C.pri : C.txS, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{m}</button>)}
    </div>

    {/* VIEW toggle: original vs cleaned (display only, does not affect comparison) */}
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
      <span style={{ fontSize: 13, color: C.txM }}>View:</span>
      <div style={{ display: "inline-flex", border: `1px solid ${C.bd}`, borderRadius: 8, overflow: "hidden" }}>
        <button onClick={() => setShowCleaned(false)} style={{ padding: "6px 14px", border: "none", background: !showCleaned ? C.pri : C.wh, color: !showCleaned ? C.wh : C.txS, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Original</button>
        <button onClick={() => setShowCleaned(true)} style={{ padding: "6px 14px", border: "none", background: showCleaned ? C.pri : C.wh, color: showCleaned ? C.wh : C.txS, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Cleaned</button>
      </div>
    </div>

    <div className="g1-m" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
      {/* ─── Left: Input ─── */}
      <div style={{ background: C.wh, borderRadius: 12, padding: 24, border: `1px solid ${C.bd}` }}>
        {mode === 3 ? <>
          {/* Mode 3: Workspace signature vs uploaded custom signature */}
          {sigs.length > 0 ? <>
            <SigPicker items={sigs} selected={selA} onSelect={setSelA} label="Document Signature" />
            <div style={{ marginTop: 12, padding: 16, background: C.off, borderRadius: 10, border: `1px solid ${C.bdLt}` }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: C.txS, marginBottom: 8 }}>Upload Signature to Compare</div>
              {uploadSigB ? <div style={{ position: "relative", display: "inline-block" }}>
                <img src={getSigImg(uploadSigB, "upload_B")} alt="Uploaded" style={{ width: "100%", maxWidth: 240, height: 130, objectFit: "contain", borderRadius: 8, background: C.wh, border: `2px solid ${C.ok}` }} />
                <button onClick={() => { setUploadSigB(null); setResult(null); }} style={{ position: "absolute", top: 4, right: 4, background: C.wh, border: `1px solid ${C.bd}`, borderRadius: "50%", width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 12 }}>✕</button>
              </div> : <UploadZone accept="JPEG, PNG" onFiles={f => handleSigUpload(f, "B")} compact />}
              {uploadSigB && <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.txS, marginBottom: 6 }}>Cleaning to apply to the uploaded signature</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {[{ v: "none", l: "None" }, { v: "model_s", l: "Basic" }, { v: "model_g", l: "Stamp" }].map(o => <button key={o.v} onClick={() => setUploadCleanMethod(o.v)} style={{ padding: "5px 12px", borderRadius: 7, border: `1px solid ${uploadCleanMethod === o.v ? C.pri : C.bd}`, background: uploadCleanMethod === o.v ? C.priPl : C.wh, color: uploadCleanMethod === o.v ? C.pri : C.txS, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{o.l}</button>)}
                </div>
                {uploadCleanMethod === "model_g" && <div style={{ fontSize: 11, color: C.txM, marginTop: 6 }}>Stamp cleaning runs a heavier model — it can take several seconds.</div>}
                {comparing && busyMsg && <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8, background: C.wh, border: `1px solid ${C.bd}`, borderRadius: 8, padding: "8px 10px" }}>
                  <div style={{ width: 12, height: 12, border: `2px solid ${C.bdLt}`, borderTopColor: C.pri, borderRadius: "50%", animation: "spin .7s linear infinite", flexShrink: 0 }} />
                  <span style={{ fontSize: 11.5, color: C.txS }}>{busyMsg}</span>
                </div>}
              </div>}
            </div>
          </> : <div style={{ textAlign: "center", padding: 40 }}>
            <div style={{ marginBottom: 16, transform: "scale(2.5)", opacity: 0.5, display: "inline-block" }}>{ic.detect(C.txD)}</div>
            <p style={{ color: C.txM, fontSize: 16, marginTop: 12 }}>Extract signatures from a document first in the Extraction tab</p>
          </div>}
        </> : sigs.length > 0 ? <>
          <SigPicker items={sigs} selected={selA} onSelect={setSelA} label={mode === 2 ? "Document A — Select Signature" : "Select Signature"} />

          {/* Mode 0: Customer ID input */}
          {mode === 0 && <div style={{ marginTop: 8 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: C.txS, display: "block", marginBottom: 5 }}>Customer ID</label>
            <input value={customerId} onChange={e => setCustomerId(e.target.value)} placeholder="Enter customer ID to compare against" style={{ width: "100%", padding: "10px 12px", background: C.off, border: `1px solid ${C.bd}`, borderRadius: 8, fontSize: 14, color: C.tx }} />
          </div>}

          {/* Mode 1: Second sig picker from same document */}
          {mode === 1 && sigs.length >= 2 && <SigPicker items={sigs.filter((_, i) => i !== selA)} selected={sigs.filter((_, i) => i !== selA).findIndex(s => sigs.indexOf(s) === selB)} onSelect={idx => { const filtered = sigs.filter((_, i) => i !== selA); setSelB(sigs.indexOf(filtered[idx])); }} label="Compare With" />}
          {mode === 1 && sigs.length < 2 && <div style={{ padding: 16, background: `${C.warn}08`, borderRadius: 8, fontSize: 13, color: C.warn, marginTop: 8 }}>Need at least 2 detected signatures to compare within document</div>}

          {/* Mode 2: Upload Document B */}
          {mode === 2 && <div style={{ marginTop: 12, padding: 16, background: C.off, borderRadius: 10, border: `1px solid ${C.bdLt}` }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.txS, marginBottom: 8 }}>Document B</div>
            {docBSigs.length > 0 ? <>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                {docBSigs.map(s => <div key={s.sigNo} style={{ padding: 4, borderRadius: 6, border: `1px solid ${C.bd}`, background: C.wh }}>
                  <img src={getSigImg(s, `docB_${s.sigNo}`)} alt={`B#${s.sigNo}`} style={{ width: 130, height: 75, objectFit: "contain", borderRadius: 6, background: C.off }} />
                  <div style={{ fontSize: 12, textAlign: "center", color: C.txM }}>B#{s.sigNo}</div>
                </div>)}
              </div>
              <div style={{ fontSize: 11, color: C.ok, fontWeight: 600 }}>{docBSigs.length} signature(s) detected</div>
              <button onClick={() => { setDocBFile(null); setDocBSigs([]); setResult(null); }} style={{ marginTop: 6, padding: "3px 10px", border: `1px solid ${C.bd}`, borderRadius: 6, background: C.wh, fontSize: 11, color: C.txM, cursor: "pointer" }}>Change Document B</button>
            </> : detectingB ? <div style={{ textAlign: "center", padding: 16 }}><div style={{ width: 24, height: 24, border: `2px solid ${C.bd}`, borderTopColor: C.pri, borderRadius: "50%", animation: "spin .7s linear infinite", margin: "0 auto 8px" }} /><span style={{ fontSize: 12, color: C.txM }}>Detecting signatures...</span></div> : <UploadZone accept="PDF, JPEG, PNG" onFiles={handleUploadDocB} compact />}
          </div>}
        </> : <div style={{ textAlign: "center", padding: 40 }}>
          <div style={{ marginBottom: 16, transform: "scale(2.5)", opacity: 0.5, display: "inline-block" }}>{ic.detect(C.txD)}</div>
          <p style={{ color: C.txM, fontSize: 16, marginTop: 12 }}>Extract signatures first in the Extraction tab</p>
        </div>}

        {sigs.length > 0 && <>
          <div style={{ marginTop: 14, padding: "10px 12px", background: `${C.ok}0c`, border: `1px solid ${C.ok}30`, borderRadius: 8, fontSize: 12.5, color: C.txS }}>
            {mode === 3
              ? <>🧹 Document signatures are cleaned <b>automatically</b>. The uploaded signature uses the method you picked above.</>
              : <>🧹 Cleaning is <b>automatic</b>: stamp cleaning if the signature overlaps a stamp, otherwise basic cleaning.</>}
          </div>
          <Btn sx={{ width: "100%", marginTop: 10 }} onClick={handleCompare}
          ch={comparing ? <><div style={{ width: 18, height: 18, border: "2px solid rgba(255,255,255,.3)", borderTopColor: C.wh, borderRadius: "50%", animation: "spin .7s linear infinite" }} /> Comparing...</> : <>{ic.compare(C.wh)} Compare</>} />
          {comparing && busyMsg && <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8, background: C.off, border: `1px solid ${C.bd}`, borderRadius: 8, padding: "8px 12px" }}>
            <div style={{ width: 13, height: 13, border: `2px solid ${C.bdLt}`, borderTopColor: C.pri, borderRadius: "50%", animation: "spin .7s linear infinite", flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: C.txS }}>{busyMsg}</span>
          </div>}
        </>}
      </div>

      {/* ─── Right: Results ─── */}
      <div style={{ background: C.wh, borderRadius: 12, padding: 24, border: `1px solid ${C.bd}` }}>
        {result ? <>
          {/* ── vs Database ── */}
          {result.type === "db" && <>
            <h3 style={{ fontFamily: "'Outfit'", fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Database Matches</h3>
            <div style={{ fontSize: 12, color: C.txM, marginBottom: 14 }}>Customer: {customerId} · {result.data.matches?.length || 0} result(s)</div>
            {result.data.matches?.length > 0 ? <div style={{ maxHeight: 500, overflowY: "auto" }}>
              {result.data.matches.map((m, i) => <div key={i} style={{ padding: 12, background: i === 0 ? `${C.pri}06` : C.off, borderRadius: 8, border: `1px solid ${i === 0 ? C.pri + "30" : C.bdLt}`, marginBottom: 8, display: "flex", gap: 12, alignItems: "center" }}>
                {m.signature_base64 && <img src={mergedCache[`db_${m.id}`] ? mergedCache[`db_${m.id}`] : (m.signature_base64.startsWith("data:") ? m.signature_base64 : `data:image/png;base64,${m.signature_base64}`)} alt="" style={{ width: 140, height: 80, objectFit: "contain", borderRadius: 6, background: C.wh, border: `1px solid ${C.bd}`, flexShrink: 0 }} />}
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>DB #{m.id}</span>
                    <SimDisplay sim={m.similarity} size="md" />
                  </div>
                  <div style={{ fontSize: 11, color: C.txM, marginTop: 2 }}>{m.customer_id && `Customer: ${m.customer_id}`}{m.tckno && ` · TCKNO: ${m.tckno}`}</div>
                </div>
              </div>)}
            </div> : <div style={{ textAlign: "center", padding: 40, color: C.txM }}>No signatures found for Customer ID: {customerId}</div>}
          </>}

          {/* ── Within Document ── */}
          {result.type === "two" && <div style={{ textAlign: "center" }}>
            {/* Before/After images */}
            <div style={{ display: "flex", gap: 12, justifyContent: "center", marginBottom: 20 }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 11, color: C.txD, fontWeight: 600, marginBottom: 4 }}>SIG #{result.sigA?.sigNo}</div>
                <img src={getSigImg(result.sigA, `ws_${result.sigA?.sigNo}`)} alt="" style={{ width: 200, height: 110, objectFit: "contain", borderRadius: 8, background: C.off, border: `1px solid ${C.bd}` }} />
              </div>
              <div style={{ display: "flex", alignItems: "center", fontSize: 24, color: C.txD }}>↔</div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 11, color: C.txD, fontWeight: 600, marginBottom: 4 }}>SIG #{result.sigB?.sigNo}</div>
                <img src={result.sigB?.sigNo === "U" ? getSigImg(result.sigB, "upload_B") : getSigImg(result.sigB, `ws_${result.sigB?.sigNo}`)} alt="" style={{ width: 200, height: 110, objectFit: "contain", borderRadius: 8, background: C.off, border: `1px solid ${C.bd}` }} />
              </div>
            </div>
            <div style={{ fontSize: 11, fontWeight: 600, color: C.txD, textTransform: "uppercase", letterSpacing: 1.5 }}>Similarity</div>
            <div style={{ marginTop: 4 }}><SimDisplay sim={result.data.similarity} size="lg" /></div>
            <div style={{ display: "inline-block", padding: "4px 14px", borderRadius: 8, fontSize: 14, fontWeight: 700, marginTop: 10, background: result.data.is_match ? `${C.ok}15` : `${C.err}15`, color: result.data.is_match ? C.ok : C.err }}>{result.data.is_match ? "MATCH" : "NO MATCH"}</div>
            <ComparisonFeedback
              key={`fb-${result.sigA?.sigNo}-${result.sigB?.sigNo}-${result.data.similarity}`}
              sig1={result.sigA?.data || result.sigA?.imageBase64}
              sig2={result.sigB?.data || result.sigB?.imageBase64}
              similarity={result.data.similarity}
              confidence={result.data.confidence}
              isMatch={result.data.is_match}
              user={user}
              notify={notify}
            />
          </div>}

          {/* ── Two Documents ── */}
          {result.type === "twodoc" && <>
            <h3 style={{ fontFamily: "'Outfit'", fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Cross-Document Results</h3>
            <div style={{ fontSize: 12, color: C.txM, marginBottom: 14 }}>Signature A#{result.sigA?.sigNo} vs {result.matches.length} signature(s) from Document B</div>
            {result.matches.length > 0 ? <div style={{ maxHeight: 500, overflowY: "auto" }}>
              {result.matches.map((m, i) => <div key={i} style={{ padding: 12, background: i === 0 ? `${C.pri}06` : C.off, borderRadius: 8, border: `1px solid ${i === 0 ? C.pri + "30" : C.bdLt}`, marginBottom: 8 }}>
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <img src={mergedCache[`docB_${m.sigBNo}`] ? mergedCache[`docB_${m.sigBNo}`] : m.sigBImg} alt="" style={{ width: 140, height: 80, objectFit: "contain", borderRadius: 6, background: C.wh, border: `1px solid ${C.bd}`, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>Doc B #{m.sigBNo}</span>
                      <SimDisplay sim={m.similarity || 0} size="md" />
                    </div>
                    <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                      <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, fontWeight: 600, background: m.is_match ? `${C.ok}15` : `${C.err}15`, color: m.is_match ? C.ok : C.err }}>{m.is_match ? "MATCH" : "NO MATCH"}</span>
                    </div>
                  </div>
                </div>
              </div>)}
            </div> : <div style={{ textAlign: "center", padding: 40, color: C.txM }}>No comparison results</div>}
          </>}
        </> : <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 400, flex: 1 }}>
          {comparing ? <><div style={{ width: 44, height: 44, border: `3px solid ${C.bd}`, borderTopColor: C.pri, borderRadius: "50%", animation: "spin .7s linear infinite", marginBottom: 16 }} /><p style={{ color: C.txM, fontSize: 16 }}>Analyzing...</p></> :
          <><div style={{ marginBottom: 16, transform: "scale(2.5)", opacity: 0.5 }}>{ic.compare(C.txD)}</div><div style={{ fontSize: 18, color: C.txM, marginTop: 12 }}>Comparison Result</div><p style={{ color: C.txM, fontSize: 15, marginTop: 16, textAlign: "center", maxWidth: 260 }}>{mode === 0 ? "Select a signature and enter Customer ID" : mode === 1 ? "Select two signatures from the document" : mode === 3 ? "Select a document signature and upload one to compare" : "Upload Document B to compare signatures"}</p></>}
        </div>}
      </div>
    </div>
  </P>;
}

// ═══════════════════════════════════════
// MAIN DASHBOARD
// ═══════════════════════════════════════
export default function DashboardPage({ go }) {
  const [tab, setTab] = useState("home");
  const [mob, setMob] = useState(false);
  const user = getUser();

  const notify = (msg, type = "info") => emitToast(msg, type);

  // ─── WORKSPACE STATE ───
  const [workspace, setWs] = useState({ document: null, signatures: [], cleanedSignatures: [], isProcessing: false, detectionDone: false, aligned: false, stamps: [], stampsDetecting: false });
  const [selectedPage, setSelectedPage] = useState(1);
  const [selectedSigs, setSelectedSigs] = useState([]);
  const [useAlign, setUseAlign] = useState(false);
  const [sigCleanCache, setSigCleanCache] = useState({}); // parent-level: survives tab switches + AddSig re-renders
  const [cleanProg, setCleanProg] = useState({ done: 0, total: 0 }); // smart-cleaning progress for the Extraction screen

  // Auto-clean extracted signatures using the backend's per-signature recommendation:
  // cleaning_method "stamp" → model_g (stamp cleaning), else model_s (basic).
  useEffect(() => {
    const sigs = workspace.signatures;
    if (sigs.length === 0) return;
    const stamps = workspace.stamps || [];
    const overlapsStamp = (sig) => {
      if (!sig?.bbox || stamps.length === 0) return false;
      const [ax1, ay1, ax2, ay2] = sig.bbox;
      return stamps.some(st => {
        if (st.page && sig.page && st.page !== sig.page) return false;
        const b = st.bbox; if (!b) return false;
        const [bx1, by1, bx2, by2] = b;
        return ax1 < bx2 && ax2 > bx1 && ay1 < by2 && ay2 > by1;
      });
    };
    // If the backend didn't tag methods AND stamps aren't resolved yet, wait (fallback path only).
    const needsFallback = sigs.some(s => !s.cleaning_method);
    if (needsFallback && workspace.stampsDetecting) return;
    const toClean = sigs.filter(s => !sigCleanCache[`ws_${s.sigNo}`] && (s.data || s.imageBase64));
    if (toClean.length === 0) { setCleanProg(p => (p.total ? { done: p.total, total: p.total } : p)); return; }
    (async () => {
      setCleanProg({ done: 0, total: toClean.length });
      const nc = { ...sigCleanCache };
      let changed = false;
      let done = 0;
      for (const s of toClean) {
        // Prefer the backend recommendation; fall back to frontend stamp overlap.
        const useStamp = s.cleaning_method ? (s.cleaning_method === "stamp") : overlapsStamp(s);
        const method = useStamp ? "model_g" : "model_s";
        try {
          const res = await fetch(`${API_BASE}/api/clean_signature`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ signature_base64: s.data || s.imageBase64, clean_lines: false, method })
          });
          if (res.ok) { const d = await res.json(); if (d.cleaned_signature) { nc[`ws_${s.sigNo}`] = d.cleaned_signature; changed = true; } }
        } catch {}
        done++;
        setCleanProg({ done, total: toClean.length });
      }
      if (changed) setSigCleanCache(nc);
    })();
  }, [workspace.signatures, workspace.stamps, workspace.stampsDetecting]);


  // ─── DOCUMENT LOAD ───
  const loadDocument = useCallback(async (file) => {
    console.log(">>> loadDocument called:", file.name, file.type, file.size);
    for (const k in _pageImgCache) delete _pageImgCache[k];
    const dataUrl = await toBase64(file);
    const isImage = file.type.startsWith("image/");
    let pageCount = 1, naturalWidth = 800, naturalHeight = 600;
    if (isImage) { const img = new window.Image(); img.src = dataUrl; await new Promise(r => { img.onload = r; }); naturalWidth = img.naturalWidth; naturalHeight = img.naturalHeight; }
    if (file.type === "application/pdf") pageCount = 0;
    console.log(">>> Document loaded, pageCount:", pageCount, "isImage:", isImage);
    setWs(prev => ({ ...prev, document: { name: file.name, type: file.type, size: file.size, dataUrl, pageCount, naturalWidth, naturalHeight, file }, signatures: [], cleanedSignatures: [] }));
    setSelectedPage(1); setSelectedSigs([]);
  }, []);

  // Does a signature bbox overlap any detected stamp on the same page?
  const sigOverlapsStamp = (sig) => {
    const stamps = workspace.stamps || [];
    if (!sig?.bbox || stamps.length === 0) return false;
    const [ax1, ay1, ax2, ay2] = sig.bbox;
    return stamps.some(st => {
      if (st.page && sig.page && st.page !== sig.page) return false;
      const b = st.bbox; if (!b) return false;
      const [bx1, by1, bx2, by2] = b;
      return ax1 < bx2 && ax2 > bx1 && ay1 < by2 && ay2 > by1;
    });
  };

  const clearWorkspace = useCallback(() => { setWs({ document: null, signatures: [], cleanedSignatures: [], isProcessing: false, detectionDone: false, aligned: false, stamps: [], stampsDetecting: false }); setSelectedPage(1); setSelectedSigs([]); setSigCleanCache({}); setCleanProg({ done: 0, total: 0 }); }, []);

  // ─── REAL API: DETECT SIGNATURES ───
  const detectSignatures = useCallback(async (opts = {}) => {
    if (!workspace.document?.file) return;
    setWs(prev => ({ ...prev, isProcessing: true }));
    const formData = new FormData();
    formData.append("pdf", workspace.document.file);
    formData.append("filename", workspace.document.file.name);
    formData.append("model_type", "model2");
    formData.append("use_alignment", opts.useAlignment ? "true" : "false");
    if (user.companyId) formData.append("company_id", user.companyId);
    if (user.id) formData.append("user_id", user.id);

    try {
      const res = await fetch(`${API_BASE}/api/extract_signatures`, { method: "POST", body: formData });
      if (!res.ok) throw new Error("Failed to extract signatures");
      const data = await res.json();
      console.log(">>> Detection API response:", { page_count: data.page_count, sig_count: data.signatures?.length, keys: Object.keys(data) });

      // ── Smart step: auto-detect stamps on the same document (non-blocking) ──
      (async () => {
        try {
          setWs(prev => ({ ...prev, stamps: [], stampsDetecting: true }));
          const sf = new FormData();
          sf.append("file", workspace.document.file);
          if (user.companyId) sf.append("company_id", user.companyId);
          if (user.id) sf.append("user_id", user.id);
          const sres = await fetch(`${API_BASE}/api/detect_stamps`, { method: "POST", body: sf });
          if (sres.ok) {
            const sdata = await sres.json();
            setWs(prev => ({ ...prev, stamps: sdata.stamps || [], stampsDetecting: false }));
            if ((sdata.stamp_count || 0) > 0) notify(`${sdata.stamp_count} stamp(s)/seal(s) detected — see the Stamp Detection tab`, "info");
          } else { setWs(prev => ({ ...prev, stampsDetecting: false })); }
        } catch { setWs(prev => ({ ...prev, stampsDetecting: false })); }
      })();

      if (!data.signatures || data.signatures.length === 0) {
        notify("No signatures found in the document.", "warning");
        setWs(prev => ({ ...prev, isProcessing: false, detectionDone: true, aligned: !!opts.useAlignment, document: { ...prev.document, pageCount: data.page_count || prev.document.pageCount || 1 } }));
        return;
      }

      const sigs = data.signatures.map((s, i) => ({
        sigNo: i + 1,
        id: s.id,
        page: s.page || 1,
        bbox: s.bbox || null,
        confidence: s.confidence || null,
        imageBase64: s.data || s.image_base64 || null,
        data: s.data || null,
        signature_type: s.signature_type || "unknown",
        signature_position: s.signature_position || "unknown",
        position_ratio: s.position_ratio || 0,
        cleaning_method: s.cleaning_method || null,   // backend smart recommendation: "stamp" | "basic"
        overlaps_stamp: !!s.overlaps_stamp,
      }));

      setWs(prev => ({ ...prev, signatures: sigs, isProcessing: false, detectionDone: true, aligned: !!opts.useAlignment, document: { ...prev.document, pageCount: data.page_count || prev.document.pageCount || 1 } }));
      notify(`${sigs.length} signature(s) detected successfully!`, "success");
    } catch (e) {
      console.error("Detection error:", e);
      notify("Error detecting signatures. " + e.message, "error");
      setWs(prev => ({ ...prev, isProcessing: false }));
    }
  }, [workspace.document]);

  // ─── REAL API: ADD SIGNATURE ───
  const addSignature = useCallback(async (sig, fields) => {
    if (!fields.customer_id && !fields.tckno && !fields.vkn) {
      notify("Please enter at least one ID (Customer ID, TCKNO, or VKN)", "error"); return;
    }
    const formData = new FormData();
    if (fields.customer_id) formData.append("customer_id", fields.customer_id);
    if (fields.tckno) formData.append("tckno", fields.tckno);
    if (fields.vkn) formData.append("vkn", fields.vkn);
    formData.append("signature_base64", sig.data || sig.imageBase64);
    if (fields.cleaning) formData.append("cleaning", fields.cleaning);
    formData.append("company_id", user.companyId);
    if (user.branchId) formData.append("branch_id", user.branchId);
    formData.append("user_id", user.id);

    try {
      const res = await fetch(`${API_BASE}/api/add_signature`, { method: "POST", body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to add signature");
      notify(`Signature registered successfully (ID: ${data.id})`, "success");
      return true;
    } catch (e) {
      notify(e.message, "error"); return false;
    }
  }, [user]);

  // ─── REAL API: FIND MATCHING ───
  const findMatching = useCallback(async (signatureData, threshold = 0.85) => {
    try {
      const res = await fetch(`${API_BASE}/api/find_matching_signature`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ detected_signature: signatureData, threshold }),
      });
      if (!res.ok) throw new Error("Search failed");
      return await res.json();
    } catch (e) { notify("Search error: " + e.message, "error"); return null; }
  }, []);

  const toggleSigSelect = (sigNo) => setSelectedSigs(prev => prev.includes(sigNo) ? prev.filter(s => s !== sigNo) : [...prev, sigNo]);

  // ─── LAYOUT ───
  const TN = { home: "Home", dashboard: "Dashboard", alerts: "Alerts", reports: "Reports", extract: "Signature Extraction", absence: "Signature Absence Check", compare: "Signature Comparison", bclean: "Basic Signature Cleaning", sclean: "Stamp Cleaning", stampdet: "Stamp Detection", addsig: "Add Signature", search: "Signature Search", doctype: "Document Type Detection", align: "Document Alignment", batch: "Batch Processing", activity: "Activity Log", settings: "Settings", help: "Help", policy: "Policy" };

  // Standalone icons for Help & Policy (self-contained so they don't depend on the icon set)
  const helpIcon = (c) => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>;
  const policyIcon = (c) => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></svg>;
  const alertsIcon = (c) => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>;
  const reportsIcon = (c) => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>;
  const dashboardIcon = (c) => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="9" rx="1" /><rect x="14" y="3" width="7" height="5" rx="1" /><rect x="14" y="12" width="7" height="9" rx="1" /><rect x="3" y="16" width="7" height="5" rx="1" /></svg>;
  const SM = [
    { header: null, items: [
      { id: "home", i: ic.home, l: "Home" },
      { id: "dashboard", i: dashboardIcon, l: "Dashboard" },
      { id: "alerts", i: alertsIcon, l: "Alerts" },
      { id: "reports", i: reportsIcon, l: "Reports" },
      { id: "activity", i: ic.activity, l: "Activity Log" },
    ] },
    { header: "Signature Detection", items: [
      { id: "absence", i: ic.search, l: "Absence Check" },
      { id: "extract", i: ic.detect, l: "Extraction" },
      { id: "stampdet", i: ic.sclean, l: "Stamp Detection" },
    ]},
    { header: "Comparison", items: [
      { id: "compare", i: ic.compare, l: "Comparison" },
    ]},
    { header: "Cleaning", items: [
      { id: "bclean", i: ic.bclean, l: "Basic Cleaning" },
      { id: "sclean", i: ic.sclean, l: "Stamp Cleaning" },
    ]},
    { header: "Document Intelligence", items: [
      { id: "doctype", i: ic.docType, l: "Doc Type" },
      { id: "align", i: ic.align, l: "Alignment" },
    ]},
    { header: "Batch Processing", items: [
      { id: "batch", i: ic.batch, l: "Batch Processing" },
    ]},
  ];
  const SO = [{ id: "addsig", i: ic.addSig, l: "Add Signature" }, { id: "search", i: ic.search, l: "Signature DB" }, { id: "help", i: helpIcon, l: "Help" }, { id: "policy", i: policyIcon, l: "Policy" }, { id: "settings", i: ic.settings, l: "Settings" }];

  // Sidebar nav button (AIATUS navy theme — white/translucent on navy)
  const navBtn = (it, click) => {
    const active = tab === it.id;
    return <button key={it.id} onClick={() => { setTab(it.id); click && click(); }} style={{ width: "100%", display: "flex", alignItems: "center", gap: 11, padding: "11px 18px", background: active ? "rgba(255,255,255,.14)" : "transparent", borderTop: "none", borderRight: "none", borderBottom: "none", borderLeft: active ? `3px solid ${C.wh}` : "3px solid transparent", color: active ? C.wh : "rgba(255,255,255,.72)", fontSize: 15, fontWeight: active ? 700 : 500, transition: "all .15s", cursor: "pointer" }}>{it.i(active ? C.wh : "rgba(255,255,255,.72)")} {it.l}</button>;
  };
  const secHeader = (txt) => <div style={{ padding: "16px 16px 5px", fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,.45)", letterSpacing: 1.4, textTransform: "uppercase" }}>{txt}</div>;

  const SB = ({ onClick: click }) => <>
    {SM.map((section, si) => <div key={si}>
      {section.header && secHeader(section.header)}
      {section.items.map(it => navBtn(it, click))}
    </div>)}
    {secHeader("Other")}
    {SO.map(it => navBtn(it, click))}
  </>;

  const stC = s => s === "completed" ? C.ok : s === "processing" ? C.warn : C.err;
  const [dashStats, setDashStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [actLoading, setActLoading] = useState(false);

  const fetchDashStats = async () => {
    try {
      const params = user.companyId ? `?company_id=${user.companyId}` : "";
      const res = await fetch(`${API_BASE}/api/dashboard/stats${params}`);
      if (res.ok) { const d = await res.json(); if (d.status === "success") setDashStats({ ...d.stats, daily: d.daily || [], top_customers: d.top_customers || [] }); }
    } catch (e) { console.warn("Dashboard stats fetch failed", e); }
  };

  const fetchActivities = async (limit = 50) => {
    setActLoading(true);
    try {
      const params = user.companyId ? `?company_id=${user.companyId}&limit=${limit}` : `?limit=${limit}`;
      const res = await fetch(`${API_BASE}/api/activity_log${params}`);
      if (res.ok) { const d = await res.json(); if (d.status === "success") setActivities(d.activities || []); }
    } catch (e) { console.warn("Activity log fetch failed", e); }
    setActLoading(false);
  };

  useEffect(() => { fetchDashStats(); fetchActivities(10); }, []);

  // Fetch the right dataset ONLY when the active tab changes.
  // This replaces per-screen fetch-on-mount effects, which caused an infinite
  // remount→fetch→setState→re-render loop (constant "refresh") because these
  // screens are re-created on every parent render.
  useEffect(() => {
    if (tab === "dashboard") { fetchDashStats(); fetchActivities(50); }
    else if (tab === "reports") { fetchDashStats(); fetchActivities(100); }
    else if (tab === "activity" || tab === "alerts") { fetchActivities(100); }
  }, [tab]);

  const actTypeLabel = t => ({ extraction: "Extract", comparison: "Compare", cleaning: "Cleaning", classification: "Doc Type", alignment: "Align", forgery_check: "Forgery", batch: "Batch" }[t] || t);
  const actTypeEmoji = t => ({ extraction: "✂️", comparison: "🔍", cleaning: "🧹", classification: "📋", alignment: "🔄", forgery_check: "🛡️", batch: "📦" }[t] || "📌");

  const JT = ({ d }) => <div style={{ overflowX: "auto" }}><table style={{ width: "100%", borderCollapse: "collapse", minWidth: 520 }}><thead><tr style={{ borderBottom: `1px solid ${C.bd}` }}>{["", "Type", "Document", "Details", "Status", "Time"].map(h => <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontSize: 12, fontWeight: 600, color: C.txD, textTransform: "uppercase" }}>{h}</th>)}</tr></thead><tbody>{d.length === 0 ? <tr><td colSpan={6} style={{ padding: 40, textAlign: "center", color: C.txM }}>No activity yet</td></tr> : d.map((j, i) => <tr key={i} style={{ borderBottom: `1px solid ${C.bdLt}` }}><td style={{ padding: "10px 12px", fontSize: 18 }}>{actTypeEmoji(j.action_type)}</td><td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 600 }}>{actTypeLabel(j.action_type)}</td><td style={{ padding: "10px 12px", fontSize: 13, color: C.txM, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{j.document_name || "—"}</td><td style={{ padding: "10px 12px", fontSize: 12, color: C.txD, maxWidth: 250, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{j.details || "—"}</td><td style={{ padding: "10px 12px" }}><span style={{ fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 6, color: stC(j.status), background: `${stC(j.status)}10` }}>{j.status}</span></td><td style={{ padding: "10px 12px", fontSize: 12, color: C.txD }}>{j.elapsed_sec ? `${j.elapsed_sec}s` : j.created_at ? new Date(j.created_at).toLocaleString("tr-TR", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" }) : "—"}</td></tr>)}</tbody></table></div>;

  // ═══ HOME ═══
  const Home = () => <P title={`Welcome back, ${user.firstName}`} sub={`${user.companyName} · Signature verification overview`}>
    <div className="stat-g" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, marginBottom: 26 }}>{[{ l: "Signatures", v: dashStats ? dashStats.total_signatures.toLocaleString() : "—", c: C.pri, e: "🖊️" }, { l: "Operations (30d)", v: dashStats ? dashStats.total_operations.toLocaleString() : "—", c: C.ok, e: "📊" }, { l: "Documents", v: dashStats ? dashStats.documents_processed.toLocaleString() : "—", c: C.acc, e: "📄" }, { l: "Today", v: dashStats ? dashStats.today_operations.toLocaleString() : "—", c: C.org, e: "📌" }].map((s, i) => <div key={i} style={{ background: C.wh, borderRadius: 14, padding: 26, border: `1px solid ${C.bd}` }}><div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}><div><div style={{ fontSize: 16, fontWeight: 600, color: C.txM, marginBottom: 8 }}>{s.l}</div><div style={{ fontFamily: "'Outfit'", fontSize: 40, fontWeight: 800, color: C.tx, lineHeight: 1 }}>{s.v}</div></div><div style={{ width: 52, height: 52, borderRadius: 13, background: `${s.c}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, flexShrink: 0 }}>{s.e}</div></div></div>)}</div>
    {/* Pipeline */}
    <div style={{ background: C.wh, borderRadius: 12, padding: 22, border: `1px solid ${C.bd}`, marginBottom: 24 }}>
      <h3 style={{ fontFamily: "'Outfit'", fontSize: 19, fontWeight: 700, marginBottom: 18 }}>Processing Pipeline</h3>
      <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
        {[{ l: "Upload", c: C.pri, i: "📄" }, { l: "Align", c: C.purp, i: "🔄" }, { l: "Extract", c: C.ok, i: "✂️" }, { l: "Clean", c: C.warn, i: "🧹" }, { l: "Compare", c: C.acc, i: "🔍" }, { l: "Result", c: C.ok, i: "✅" }].map((s, i, arr) => <span key={i} style={{ display: "contents" }}>
          <div onClick={() => setTab(i === 0 ? "extract" : i === 1 ? "align" : i === 2 ? "extract" : i === 3 ? "bclean" : i === 4 ? "compare" : "extract")} style={{ flex: 1, textAlign: "center", padding: "14px 4px", borderRadius: 10, cursor: "pointer", transition: "all .15s" }} onMouseOver={e => e.currentTarget.style.background = `${s.c}08`} onMouseOut={e => e.currentTarget.style.background = "transparent"}>
            <div style={{ fontSize: 30, marginBottom: 7 }}>{s.i}</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: s.c }}>{s.l}</div>
          </div>
          {i < arr.length - 1 && <div style={{ color: C.pri, fontSize: 22, flexShrink: 0, opacity: 0.5 }}>→</div>}
        </span>)}
      </div>
    </div>
    <div style={{ marginBottom: 24 }}>
      <div style={{ background: C.wh, borderRadius: 12, padding: 22, border: `1px solid ${C.bd}` }}><h3 style={{ fontFamily: "'Outfit'", fontSize: 19, fontWeight: 700, marginBottom: 16 }}>Quick Actions</h3><div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 10 }}>{[{ l: "Extract", c: C.pri, i: ic.detect, t: "extract" }, { l: "Absence", c: C.acc, i: ic.search, t: "absence" }, { l: "Compare", c: C.ok, i: ic.compare, t: "compare" }, { l: "Clean", c: C.warn, i: ic.bclean, t: "bclean" }, { l: "Stamp Clean", c: C.org, i: ic.sclean, t: "sclean" }, { l: "Stamp Det.", c: C.err, i: ic.sclean, t: "stampdet" }, { l: "Add Sig.", c: C.pink, i: ic.addSig, t: "addsig" }, { l: "Doc Type", c: C.acc, i: ic.docType, t: "doctype" }, { l: "Align", c: C.purp, i: ic.align, t: "align" }, { l: "Batch", c: C.org, i: ic.batch, t: "batch" }].map((a, i) => <button key={i} onClick={() => setTab(a.t)} style={{ background: C.off, border: `1px solid ${C.bd}`, borderRadius: 10, padding: 14, display: "flex", flexDirection: "column", alignItems: "center", gap: 7, cursor: "pointer" }} onMouseOver={e => e.currentTarget.style.borderColor = a.c} onMouseOut={e => e.currentTarget.style.borderColor = C.bd}><div>{a.i(a.c)}</div><div style={{ fontSize: 14, fontWeight: 600 }}>{a.l}</div></button>)}</div></div>
    </div>
    <div style={{ background: C.wh, borderRadius: 12, border: `1px solid ${C.bd}`, overflow: "hidden" }}><div style={{ padding: "14px 18px", borderBottom: `1px solid ${C.bd}`, display: "flex", justifyContent: "space-between" }}><h3 style={{ fontFamily: "'Outfit'", fontSize: 17, fontWeight: 700 }}>Recent Activity</h3><button onClick={() => { setTab("activity"); fetchActivities(50); }} style={{ fontSize: 13, color: C.pri, background: "none", border: "none", fontWeight: 600, cursor: "pointer" }}>View All →</button></div><JT d={activities.slice(0, 5)} /></div>
  </P>;

  // ═══ DASHBOARD (operational overview — distinct from Home launcher) ═══
  const Dashboard = () => {
    const okCount = activities.filter(a => a.status === "completed").length;
    const errCount = activities.filter(a => a.status === "error" || a.status === "failed").length;
    const openAlerts = activities.filter(a => a.status && a.status !== "completed").length;
    const successRate = activities.length > 0 ? Math.round((okCount / activities.length) * 100) : 0;
    // Daily usage — last 14 days. Prefer real backend daily counts; fall back to the activity sample.
    const usageDays = (() => {
      const fmt = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
      const days = [];
      for (let i = 13; i >= 0; i--) { const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() - i); days.push({ key: fmt(d), date: d, count: 0 }); }
      const idx = {}; days.forEach((d, i) => { idx[d.key] = i; });
      const daily = (dashStats && dashStats.daily) || [];
      if (daily.length > 0) {
        daily.forEach(r => { const k = String(r.day).slice(0, 10); if (idx[k] !== undefined) days[idx[k]].count = Number(r.count) || 0; });
      } else {
        activities.forEach(a => { if (!a.created_at) return; const dt = new Date(a.created_at); if (isNaN(dt)) return; const k = fmt(dt); if (idx[k] !== undefined) days[idx[k]].count++; });
      }
      return days;
    })();
    const usageMax = Math.max(1, ...usageDays.map(d => d.count));
    const usageTotal = usageDays.reduce((s, d) => s + d.count, 0);

    const kpis = [
      { l: "Customers", v: dashStats ? (dashStats.total_customers ?? 0).toLocaleString() : "—", c: C.pri, e: "👥" },
      { l: "Avg. Signatures / Customer", v: dashStats ? (dashStats.avg_signatures_per_customer ?? 0).toLocaleString() : "—", c: C.acc, e: "🖊️" },
      { l: "Total Signatures", v: dashStats ? dashStats.total_signatures.toLocaleString() : "—", c: C.ok, e: "📝" },
      { l: "Operations (30d)", v: dashStats ? dashStats.total_operations.toLocaleString() : "—", c: C.org, e: "📊" },
    ];

    return <P title="Dashboard" sub={`${user.companyName} · operational overview`}>
      {/* KPI row */}
      <div className="stat-g" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, marginBottom: 24 }}>
        {kpis.map((s, i) => <div key={i} style={{ background: C.wh, borderRadius: 14, padding: 26, border: `1px solid ${C.bd}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 600, color: C.txM, marginBottom: 8 }}>{s.l}</div>
              <div style={{ fontFamily: "'Outfit'", fontSize: 40, fontWeight: 800, color: C.tx, lineHeight: 1 }}>{s.v}</div>
            </div>
            <div style={{ width: 52, height: 52, borderRadius: 13, background: `${s.c}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, flexShrink: 0 }}>{s.e}</div>
          </div>
        </div>)}
      </div>

      {/* Alerts placeholder + Daily usage */}
      <div className="g1-m" style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 16, marginBottom: 24 }}>
        {/* Alerts — static placeholder for now */}
        <div style={{ background: C.wh, borderRadius: 12, padding: 22, border: `1px solid ${C.bd}`, display: "flex", flexDirection: "column" }}>
          <h3 style={{ fontFamily: "'Outfit'", fontSize: 19, fontWeight: 700, marginBottom: 16 }}>Alerts</h3>
          <div style={{ flex: 1, minHeight: 200, border: `1.5px dashed ${C.bd}`, borderRadius: 10, background: C.off, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: 24 }}>
            <div style={{ marginBottom: 12, opacity: 0.45 }}>{alertsIcon(C.txD)}</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.txS, marginBottom: 4 }}>No alerts yet</div>
            <div style={{ fontSize: 13, color: C.txM, lineHeight: 1.5, maxWidth: 240 }}>Alerts will appear here. This area is a placeholder for now.</div>
          </div>
        </div>

        {/* Daily Usage */}
        <div style={{ background: C.wh, borderRadius: 12, padding: 22, border: `1px solid ${C.bd}` }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
            <h3 style={{ fontFamily: "'Outfit'", fontSize: 19, fontWeight: 700 }}>Daily Usage</h3>
            <span style={{ fontSize: 13, color: C.txM }}>last 14 days · {usageTotal} ops</span>
          </div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 180, paddingTop: 8 }}>
            {usageDays.map((d, i) => { const h = Math.round((d.count / usageMax) * 150); const isToday = i === usageDays.length - 1; return <div key={i} title={`${d.key}: ${d.count}`} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, height: "100%", justifyContent: "flex-end" }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: d.count > 0 ? C.pri : C.txD }}>{d.count || ""}</span>
              <div style={{ width: "100%", maxWidth: 26, height: Math.max(3, h), background: isToday ? C.pri : `${C.pri}66`, borderRadius: "5px 5px 0 0", transition: "height .3s" }} />
              <span style={{ fontSize: 10, color: C.txD }}>{d.date.getDate()}/{d.date.getMonth() + 1}</span>
            </div>; })}
          </div>
        </div>
      </div>

      {/* Top customers by signature count */}
      <div style={{ background: C.wh, borderRadius: 12, padding: 22, border: `1px solid ${C.bd}`, marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
          <h3 style={{ fontFamily: "'Outfit'", fontSize: 19, fontWeight: 700 }}>Top Customers by Signatures</h3>
          <button onClick={() => setTab("search")} style={{ fontSize: 13, color: C.pri, background: "none", border: "none", fontWeight: 600, cursor: "pointer" }}>View All →</button>
        </div>
        {(() => {
          const tc = (dashStats && dashStats.top_customers) || [];
          if (tc.length === 0) return <div style={{ padding: 24, textAlign: "center", color: C.txM, fontSize: 14 }}>No customer signatures recorded yet</div>;
          const maxC = Math.max(1, ...tc.map(c => Number(c.count) || 0));
          return <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {tc.map((c, i) => <div key={i}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: C.tx, display: "flex", alignItems: "center", gap: 8 }}><span style={{ width: 22, height: 22, borderRadius: 6, background: `${C.pri}12`, color: C.pri, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800 }}>{i + 1}</span>Customer #{c.customer_id}</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: C.pri }}>{c.count} signatures</span>
              </div>
              <div style={{ height: 9, background: C.bdLt, borderRadius: 5, overflow: "hidden" }}>
                <div style={{ width: `${Math.round(((Number(c.count) || 0) / maxC) * 100)}%`, height: "100%", background: C.pri, borderRadius: 5 }} />
              </div>
            </div>)}
          </div>;
        })()}
      </div>

      {/* Recent activity */}
      <div style={{ background: C.wh, borderRadius: 12, border: `1px solid ${C.bd}`, overflow: "hidden" }}>
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${C.bd}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ fontFamily: "'Outfit'", fontSize: 17, fontWeight: 700 }}>Recent Activity</h3>
          <button onClick={() => { setTab("activity"); fetchActivities(50); }} style={{ fontSize: 13, color: C.pri, background: "none", border: "none", fontWeight: 600, cursor: "pointer" }}>View All →</button>
        </div>
        <JT d={activities.slice(0, 6)} />
      </div>
    </P>;
  };

  // ═══ EXTRACTION ═══
  const Extract = () => {
    const { document: doc, signatures, isProcessing, detectionDone } = workspace;
    const [exportFmt, setExportFmt] = useState("json"); // json or csv

    const jsonData = signatures.map(s => ({ id: s.id, page: s.page, signature_no: s.sigNo, signature_type: s.signature_type || "unknown", signature_position: s.signature_position || "unknown", cleaning_method: s.cleaning_method || (sigOverlapsStamp(s) ? "stamp" : "basic"), overlaps_stamp: s.overlaps_stamp ?? sigOverlapsStamp(s), ...(s.bbox ? { bbox: { x1: s.bbox[0], y1: s.bbox[1], x2: s.bbox[2], y2: s.bbox[3] } } : {}) }));

    return <P title="Signature Extraction" sub="Upload a document to detect, extract and export signature locations">
      <DocContextBar doc={doc} sigCount={signatures.length} onClear={clearWorkspace} />

      {/* ── TOP: Input / Output side by side ── */}
      <div className="g1-m" style={{ display: "grid", gridTemplateColumns: signatures.length > 0 ? "1fr 1fr" : "1fr 1fr", gap: 16 }}>
        {/* LEFT: Input */}
        <div>{!doc ? <div style={{ background: C.wh, borderRadius: 12, padding: 24, border: `1px solid ${C.bd}` }}>
          <h3 style={{ fontFamily: "'Outfit'", fontSize: 17, fontWeight: 700, marginBottom: 16 }}>Upload Document</h3>
          <UploadZone onFiles={files => loadDocument(files[0])} />
        </div> : <>
          <div style={{ background: C.wh, borderRadius: 12, border: `1px solid ${C.bd}`, overflow: "hidden" }}>
            <div style={{ padding: "8px 14px", borderBottom: `1px solid ${C.bd}`, fontSize: 12, fontWeight: 600, color: C.txD, background: C.off }}>INPUT — {doc.name}</div>
            <div style={{ padding: 0 }}><DocPreview doc={doc} signatures={[]} selectedPage={selectedPage} onPageChange={setSelectedPage} aligned={false} /></div>
          </div>
          {signatures.length === 0 && !detectionDone && <div style={{ marginTop: 12, background: C.wh, borderRadius: 12, padding: 20, border: `1px solid ${C.bd}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: C.txM, marginBottom: 14, background: C.priGh || C.off, padding: "10px 12px", borderRadius: 8 }}>{ic.align(C.pri)} The document is aligned automatically, then signatures are extracted.</div>
            <Btn sx={{ width: "100%" }} onClick={() => detectSignatures({ useAlignment: true })} ch={isProcessing ? <><div style={{ width: 18, height: 18, border: "2px solid rgba(255,255,255,.3)", borderTopColor: C.wh, borderRadius: "50%", animation: "spin .7s linear infinite" }} /> Aligning & extracting...</> : <>{ic.detect(C.wh)} Extract Signatures</>} />
          </div>}
        </>}</div>

        {/* RIGHT: Output — document with bounding boxes */}
        <div>{signatures.length > 0 ? <div>
          <div style={{ background: C.wh, borderRadius: 12, border: `1px solid ${C.ok}30`, overflow: "hidden" }}>
            <div style={{ padding: "8px 14px", borderBottom: `1px solid ${C.bd}`, fontSize: 12, fontWeight: 600, color: C.ok, background: C.off, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>Signature Preview — {signatures.length} detected</span>
              <button onClick={clearWorkspace} style={{ background: "none", border: "none", color: C.txM, fontSize: 11, cursor: "pointer" }}>✕ Clear</button>
            </div>
            <div style={{ padding: 0 }}><DocPreview doc={doc} signatures={signatures} selectedPage={selectedPage} onPageChange={setSelectedPage} aligned={workspace.aligned} /></div>
          </div>
        </div> : !doc ? <div style={{ background: C.wh, borderRadius: 12, padding: 24, border: `1px solid ${C.bd}` }}>
          <h3 style={{ fontFamily: "'Outfit'", fontSize: 17, fontWeight: 700, marginBottom: 16 }}>Supported Formats</h3>
          {["PDF (multi-page)", "PNG / JPG / JPEG", "TIFF (multi-page)"].map((f, i) => <div key={i} style={{ display: "flex", gap: 10, marginBottom: 12 }}>{ic.check(C.ok)}<span style={{ fontSize: 14 }}>{f}</span></div>)}
          <div style={{ marginTop: 16, padding: 12, background: C.priGh, borderRadius: 8 }}><span style={{ fontSize: 13, color: C.txS }}><strong>Export:</strong> CSV and JSON with coordinates</span></div>
        </div> : detectionDone ? <div style={{ background: C.wh, borderRadius: 12, padding: 24, border: `1px solid ${C.bd}` }}>
          <div style={{ textAlign: "center", padding: "30px 20px" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
            <h3 style={{ fontFamily: "'Outfit'", fontSize: 18, fontWeight: 700, color: C.warn, marginBottom: 10 }}>No Signatures Found</h3>
            <p style={{ fontSize: 14, color: C.txM, lineHeight: 1.6, marginBottom: 16 }}>We couldn't detect any signatures. Try enabling auto-alignment or a different document.</p>
            <Btn sx={{ width: "100%" }} v="out" onClick={clearWorkspace} ch={<>{ic.upload(C.pri)} Try Another Document</>} />
          </div>
        </div> : isProcessing ? <div style={{ background: C.wh, borderRadius: 12, padding: 40, border: `1px solid ${C.bd}`, textAlign: "center" }}>
          <div style={{ width: 40, height: 40, border: `3px solid ${C.bd}`, borderTopColor: C.pri, borderRadius: "50%", animation: "spin .7s linear infinite", margin: "0 auto 16px" }} />
          <p style={{ color: C.txM, fontSize: 14 }}>Extracting signatures...</p>
        </div> : null}</div>
      </div>

      {/* ── SMART CLEANING PIPELINE PROGRESS ── */}
      {signatures.length > 0 && (() => {
        const detecting = workspace.stampsDetecting;
        const cleaning = cleanProg.total > 0 && cleanProg.done < cleanProg.total;
        const busy = detecting || cleaning;
        const pct = cleanProg.total > 0 ? Math.round((cleanProg.done / cleanProg.total) * 100) : (detecting ? 0 : 100);
        const steps = [
          { l: "Aligned", state: "done" },
          { l: "Signatures extracted", state: "done" },
          { l: "Stamp detection", state: detecting ? "run" : "done" },
          { l: "Smart cleaning", state: detecting ? "wait" : (cleaning ? "run" : "done") },
        ];
        const dot = (st) => st === "done" ? C.ok : st === "run" ? C.pri : C.bd;
        return <div style={{ marginTop: 16, background: busy ? C.wh : `${C.ok}0c`, border: `1px solid ${busy ? C.bd : `${C.ok}30`}`, borderRadius: 10, padding: "12px 14px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: C.txS, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 700, color: busy ? C.pri : C.ok }}>🧹 Smart Cleaning</span>
            <span>Each signature is cleaned with the model it needs — <b>Stamp clean</b> when it overlaps a detected stamp, otherwise <b>Basic clean</b>.</span>
            <span style={{ marginLeft: "auto", fontSize: 12, fontWeight: 600, color: busy ? C.pri : C.ok }}>
              {detecting ? "Detecting stamps…" : cleaning ? `Cleaning ${cleanProg.done}/${cleanProg.total}` : "Completed"}
            </span>
          </div>

          {/* progress bar */}
          <div style={{ height: 6, background: C.bdLt, borderRadius: 3, overflow: "hidden", marginTop: 10 }}>
            <div style={{ width: `${busy ? Math.max(pct, 6) : 100}%`, height: "100%", background: busy ? C.pri : C.ok, borderRadius: 3, transition: "width .3s ease" }} />
          </div>

          {/* step chips */}
          <div style={{ display: "flex", gap: 14, marginTop: 10, flexWrap: "wrap" }}>
            {steps.map((st, i) => <span key={i} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: st.state === "wait" ? C.txD : C.txS, fontWeight: st.state === "run" ? 700 : 500 }}>
              {st.state === "run"
                ? <span style={{ width: 11, height: 11, border: `2px solid ${C.bdLt}`, borderTopColor: C.pri, borderRadius: "50%", animation: "spin .7s linear infinite" }} />
                : <span style={{ width: 9, height: 9, borderRadius: "50%", background: dot(st.state) }} />}
              {st.l}
            </span>)}
          </div>

          {busy && <div style={{ fontSize: 11, color: C.txM, marginTop: 8 }}>Signatures are already visible — cleaning is still running in the background.</div>}
        </div>;
      })()}
      {signatures.length > 0 && <div style={{ marginTop: 12, background: C.wh, borderRadius: 12, border: `1px solid ${C.bd}`, padding: 16, display: "flex", gap: 16 }}>
        <div style={{ flex: 1, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
          {signatures.map(s => <div key={s.sigNo} onClick={() => toggleSigSelect(s.sigNo)} style={{ background: selectedSigs.includes(s.sigNo) ? C.priPl : C.off, borderRadius: 10, border: `1.5px solid ${selectedSigs.includes(s.sigNo) ? C.pri : C.bd}`, padding: 12, cursor: "pointer", transition: "all .15s", textAlign: "center" }}>
            {(s.imageBase64 || s.data) ? <img src={s.imageBase64 || s.data} alt={`Sig ${s.sigNo}`} style={{ width: "100%", height: 90, objectFit: "contain", borderRadius: 6, background: C.wh, border: `1px solid ${C.bd}` }} /> : <div style={{ width: "100%", height: 90, borderRadius: 6, background: C.wh, border: `1px solid ${C.bd}`, display: "flex", alignItems: "center", justifyContent: "center" }}>{ic.detect(C.txD)}</div>}
            <div style={{ fontSize: 15, fontWeight: 700, color: selectedSigs.includes(s.sigNo) ? C.pri : C.tx, marginTop: 8 }}>#{s.sigNo}</div>
            <div style={{ fontSize: 13, color: C.txM }}>Page {s.page || 1}</div>
            {s.signature_type && s.signature_type !== "unknown" && <div style={{ marginTop: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 4, color: s.signature_type === "customer" ? "#16a34a" : s.signature_type === "institution" ? "#2563eb" : C.txD, background: s.signature_type === "customer" ? "#16a34a12" : s.signature_type === "institution" ? "#2563eb12" : C.off }}>
                {s.signature_type === "customer" ? "Customer" : s.signature_type === "institution" ? "Institution" : s.signature_type}
              </span>
              <span style={{ fontSize: 10, color: C.txD, marginLeft: 4 }}>{s.signature_position}</span>
            </div>}
            {(() => {
              // Prefer the backend recommendation; if absent, derive it from stamp overlap.
              let cm = s.cleaning_method === "stamp" ? "stamp" : s.cleaning_method === "basic" ? "basic" : null;
              if (!cm) {
                if (workspace.stampsDetecting) return null;           // still detecting → don't guess
                cm = sigOverlapsStamp(s) ? "stamp" : "basic";
              }
              const isStamp = cm === "stamp";
              return <div style={{ marginTop: 5 }} title={isStamp ? "Overlaps a detected stamp → stamp cleaning" : "No stamp overlap → basic cleaning"}>
                <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4, color: isStamp ? C.org : C.ok, background: isStamp ? `${C.org}14` : `${C.ok}14` }}>🧹 {isStamp ? "Stamp clean" : "Basic clean"}</span>
              </div>;
            })()}
          </div>)}
        </div>
        {selectedSigs.length > 0 && <div style={{ width: 160, flexShrink: 0, display: "flex", flexDirection: "column", gap: 8, justifyContent: "center" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.pri, marginBottom: 4 }}>{selectedSigs.length} selected</div>
          <Btn s="sm" v="gh" sx={{ width: "100%" }} onClick={() => setTab("bclean")} ch={<>{ic.clean(C.pri)} Clean</>} />
          <Btn s="sm" v="gh" sx={{ width: "100%" }} onClick={() => setTab("compare")} ch={<>{ic.compare(C.pri)} Compare</>} />
          <Btn s="sm" v="gh" sx={{ width: "100%" }} onClick={() => setTab("addsig")} ch={<>{ic.addSig(C.pri)} Register</>} />
          <Btn s="sm" v="gh" sx={{ width: "100%" }} onClick={() => setTab("search")} ch={<>{ic.search(C.pri)} Search</>} />
        </div>}
      </div>}

      {/* ── BOTTOM: JSON/CSV Data Viewer ── */}
      {signatures.length > 0 && <div style={{ marginTop: 16, background: C.wh, borderRadius: 12, border: `1px solid ${C.bd}`, overflow: "hidden" }}>
        <div style={{ padding: "10px 16px", borderBottom: `1px solid ${C.bd}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: C.off }}>
          <div style={{ display: "flex", gap: 6 }}>
            {["json", "csv"].map(f => <button key={f} onClick={() => setExportFmt(f)} style={{ padding: "4px 12px", borderRadius: 6, border: `1px solid ${exportFmt === f ? C.pri : C.bd}`, background: exportFmt === f ? C.priPl : C.wh, color: exportFmt === f ? C.pri : C.txS, fontSize: 12, fontWeight: 600, cursor: "pointer", textTransform: "uppercase" }}>{f}</button>)}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {exportFmt === "json" && <Btn s="sm" onClick={() => downloadJSON(jsonData, `${doc?.name||"sig"}_signatures.json`)} ch={<>{ic.download(C.wh)} Export JSON</>} />}
            {exportFmt === "csv" && <Btn s="sm" onClick={() => downloadCSV(signatures, `${doc?.name||"sig"}_locations.csv`)} ch={<>{ic.download(C.wh)} Export CSV</>} />}
          </div>
        </div>
        <div style={{ padding: 16, maxHeight: 300, overflowY: "auto" }}>
          {exportFmt === "json" ? <pre style={{ fontFamily: "'JetBrains Mono'", fontSize: 12, color: C.tx, margin: 0, whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{JSON.stringify(jsonData, null, 2)}</pre>
          : <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead><tr style={{ borderBottom: `2px solid ${C.bd}` }}>
              {["#", "Page", "X1", "Y1", "X2", "Y2", "Width", "Height"].map(h => <th key={h} style={{ padding: "8px 10px", textAlign: "left", fontSize: 11, fontWeight: 700, color: C.txD, textTransform: "uppercase" }}>{h}</th>)}
            </tr></thead>
            <tbody>{signatures.map(s => { const b = s.bbox || [0,0,0,0]; return <tr key={s.sigNo} style={{ borderBottom: `1px solid ${C.bdLt}` }}>
              <td style={{ padding: "6px 10px", fontWeight: 600, color: C.pri }}>{s.sigNo}</td>
              <td style={{ padding: "6px 10px" }}>{s.page}</td>
              <td style={{ padding: "6px 10px", fontFamily: "'JetBrains Mono'", fontSize: 12 }}>{b[0]}</td>
              <td style={{ padding: "6px 10px", fontFamily: "'JetBrains Mono'", fontSize: 12 }}>{b[1]}</td>
              <td style={{ padding: "6px 10px", fontFamily: "'JetBrains Mono'", fontSize: 12 }}>{b[2]}</td>
              <td style={{ padding: "6px 10px", fontFamily: "'JetBrains Mono'", fontSize: 12 }}>{b[3]}</td>
              <td style={{ padding: "6px 10px", fontFamily: "'JetBrains Mono'", fontSize: 12 }}>{b[2]-b[0]}</td>
              <td style={{ padding: "6px 10px", fontFamily: "'JetBrains Mono'", fontSize: 12 }}>{b[3]-b[1]}</td>
            </tr>; })}</tbody>
          </table>}
        </div>
      </div>}
    </P>;
  };

  // ═══ ADD SIGNATURE ═══
  const AddSig = () => {
    const { signatures } = workspace;
    const hasSigs = signatures.length > 0;
    const [addMode, setAddMode] = useState(hasSigs ? "workspace" : "upload");
    const [fields, setFields] = useState({ customer_id: "", tckno: "", vkn: "", name: "" });
    const [addingSigNo, setAddingSigNo] = useState(null);
    const [uploadedSig, setUploadedSig] = useState(null);
    const [addCleaning, setAddCleaning] = useState("basic");
    const [docSigs, setDocSigs] = useState([]);      // customer signatures extracted from an uploaded document
    const [docStamps, setDocStamps] = useState([]);  // stamps detected on that document
    const [docBusy, setDocBusy] = useState(false);
    const [docSel, setDocSel] = useState(0);

    // Clean uploaded sig on upload (workspace sigs already cleaned by parent)
    useEffect(() => {
      if (!uploadedSig || sigCleanCache["add_upload"]) return;
      (async () => {
        try {
          const res = await fetch(`${API_BASE}/api/clean_signature`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ signature_base64: uploadedSig.data, clean_lines: false, method: "model_s" })
          });
          if (res.ok) { const d = await res.json(); if (d.cleaned_signature) setSigCleanCache(prev => ({ ...prev, "add_upload": d.cleaned_signature })); }
        } catch {}
      })();
    }, [uploadedSig]);

    const getCleanImg = (sig, key) => sigCleanCache[key] || sig.data || sig.imageBase64;

    // Clean a base64 signature via the API, returning cleaned base64 (or original on failure)
    const cleanViaApi = async (b64, method) => {
      try {
        const res = await fetch(`${API_BASE}/api/clean_signature`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ signature_base64: b64, clean_lines: false, method })
        });
        if (res.ok) { const d = await res.json(); return d.cleaned_signature || b64; }
      } catch {}
      return b64;
    };

    // Does the signature bbox overlap any detected stamp on the same page?
    const overlapsStamp = (sig) => {
      if (!sig.bbox || !docStamps || docStamps.length === 0) return false;
      const [ax1, ay1, ax2, ay2] = sig.bbox;
      return docStamps.some(st => {
        if (st.page && sig.page && st.page !== sig.page) return false;
        const b = st.bbox; if (!b) return false;
        const [bx1, by1, bx2, by2] = b;
        return ax1 < bx2 && ax2 > bx1 && ay1 < by2 && ay2 > by1; // rectangle intersection
      });
    };
    // Which cleaning a signature needs, based on stamp overlap: "stamp" | "basic"
    const smartCleaning = (sig) => sig?.cleaning_method ? sig.cleaning_method : (overlapsStamp(sig) ? "stamp" : "basic");

    // Upload a document → align + extract → keep ONLY customer signatures → detect stamps
    const handleUploadDoc = async (files) => {
      const file = files[0];
      setDocSigs([]); setDocStamps([]); setDocSel(0); setDocBusy(true);
      try {
        notify("Aligning document and extracting signatures...", "info");
        const fd = new FormData();
        fd.append("pdf", file); fd.append("filename", file.name); fd.append("model_type", "model2"); fd.append("use_alignment", "true");
        if (user.companyId) fd.append("company_id", user.companyId);
        if (user.id) fd.append("user_id", user.id);
        const res = await fetch(`${API_BASE}/api/extract_signatures`, { method: "POST", body: fd });
        const data = res.ok ? await res.json() : {};
        const all = (data.signatures || []).map((s, i) => ({ sigNo: i + 1, id: s.id, page: s.page || 1, bbox: s.bbox || null, data: s.data || s.image_base64 || null, imageBase64: s.data || null, signature_type: s.signature_type || "unknown", cleaning_method: s.cleaning_method || null }));
        const customers = all.filter(s => s.signature_type === "customer");
        setDocSigs(customers);
        notify("Detecting stamps...", "info");
        try {
          const sf = new FormData(); sf.append("file", file);
          const sres = await fetch(`${API_BASE}/api/detect_stamps`, { method: "POST", body: sf });
          if (sres.ok) { const sd = await sres.json(); setDocStamps(sd.stamps || []); }
        } catch {}
        if (customers.length === 0) notify("No customer signature found in the document", "warning");
        else notify(`${customers.length} customer signature(s) found`, "success");
      } catch (e) { notify("Operation failed: " + e.message, "error"); }
      setDocBusy(false);
    };

    const handleUploadNew = async (files) => {
      const file = files[0];
      const dataUrl = await toBase64(file);
      setUploadedSig({ sigNo: 1, data: dataUrl, imageBase64: dataUrl, page: 1, name: file.name });
    };

    const handleAdd = async (sig) => {
      setAddingSigNo(sig.sigNo || 1);
      let sigToAdd = sig;
      // document mode → smart (stamp overlap); other modes → the chosen cleaning
      const need = addMode === "document" ? smartCleaning(sig) : addCleaning; // "none" | "basic" | "stamp"
      if (need && need !== "none") {
        notify(`Applying cleaning: ${need === "stamp" ? "stamp" : "basic"}...`, "info");
        const cleaned = await cleanViaApi(sig.data || sig.imageBase64, need === "stamp" ? "model_g" : "model_s");
        sigToAdd = { ...sig, data: cleaned, imageBase64: cleaned };
      }
      const ok = await addSignature(sigToAdd, { ...fields, cleaning: "none" });
      if (ok) {
        setFields({ customer_id: "", tckno: "", vkn: "", name: "" });
        if (addMode === "workspace") { setWs(prev => ({ ...prev, signatures: prev.signatures.filter(s => s.sigNo !== sig.sigNo) })); }
        else if (addMode === "document") { setDocSigs(prev => prev.filter(s => s.sigNo !== sig.sigNo)); setDocSel(0); }
        else { setUploadedSig(null); }
      }
      setAddingSigNo(null);
    };

    const currentSig = addMode === "workspace" && hasSigs
      ? signatures.find(s => s.sigNo === selectedSigs[0])
      : addMode === "document"
      ? docSigs[docSel]
      : uploadedSig;

    return <P title="Add Signature" sub="Register reference signatures to database">
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {hasSigs && <button onClick={() => setAddMode("workspace")} style={{ padding: "8px 18px", borderRadius: 8, border: `1px solid ${addMode === "workspace" ? C.pri : C.bd}`, background: addMode === "workspace" ? C.priPl : C.wh, color: addMode === "workspace" ? C.pri : C.txS, fontSize: 13, fontWeight: 600 }}>From Extraction ({signatures.length})</button>}
        <button onClick={() => setAddMode("upload")} style={{ padding: "8px 18px", borderRadius: 8, border: `1px solid ${addMode === "upload" ? C.pri : C.bd}`, background: addMode === "upload" ? C.priPl : C.wh, color: addMode === "upload" ? C.pri : C.txS, fontSize: 13, fontWeight: 600 }}>Upload New</button>
        <button onClick={() => setAddMode("document")} style={{ padding: "8px 18px", borderRadius: 8, border: `1px solid ${addMode === "document" ? C.pri : C.bd}`, background: addMode === "document" ? C.priPl : C.wh, color: addMode === "document" ? C.pri : C.txS, fontSize: 13, fontWeight: 600 }}>From Document (customer)</button>
      </div>
      <div className="g1-m" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div style={{ background: C.wh, borderRadius: 12, padding: 24, border: `1px solid ${C.bd}` }}>
          {addMode === "document" ? (
            docBusy ? <div style={{ textAlign: "center", padding: 40 }}><div style={{ width: 40, height: 40, border: `3px solid ${C.bd}`, borderTopColor: C.pri, borderRadius: "50%", animation: "spin .7s linear infinite", margin: "0 auto 16px" }} /><p style={{ color: C.txM, fontSize: 15 }}>Aligning document, extracting signatures, detecting stamps...</p></div> :
            docSigs.length > 0 ? <><h3 style={{ fontFamily: "'Outfit'", fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Customer Signatures ({docSigs.length})</h3>
              <p style={{ fontSize: 12, color: C.txM, marginBottom: 12 }}>On save, signatures overlapping a stamp get stamp cleaning; the rest get basic cleaning — automatically.</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{docSigs.map((s, i) => { const need = smartCleaning(s); return <div key={s.sigNo} onClick={() => setDocSel(i)} style={{ display: "flex", gap: 12, alignItems: "center", padding: 10, borderRadius: 10, cursor: "pointer", background: docSel === i ? C.priPl : C.wh, border: `1.5px solid ${docSel === i ? C.pri : C.bd}` }}>
                {(s.data || s.imageBase64) && <img src={s.data || s.imageBase64} alt={`#${s.sigNo}`} style={{ width: 96, height: 54, objectFit: "contain", borderRadius: 6, background: C.off, border: `1px solid ${C.bd}` }} />}
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.tx }}>Signature #{s.sigNo} · Page {s.page}</div>
                  <span style={{ display: "inline-block", marginTop: 4, fontSize: 11, fontWeight: 700, padding: "2px 9px", borderRadius: 5, color: need === "stamp" ? C.org : C.ok, background: need === "stamp" ? `${C.org}14` : `${C.ok}14` }}>{need === "stamp" ? "🧹 Stamp clean" : "🧹 Basic clean"}</span>
                </div>
              </div>; })}</div>
              <button onClick={() => { setDocSigs([]); setDocStamps([]); }} style={{ marginTop: 12, width: "100%", padding: 8, background: C.off, border: `1px solid ${C.bd}`, borderRadius: 8, fontSize: 13, color: C.txM, cursor: "pointer" }}>Another Document</button></> :
            <><h3 style={{ fontFamily: "'Outfit'", fontSize: 17, fontWeight: 700, marginBottom: 8 }}>Upload Document — Customer Signatures</h3>
              <p style={{ fontSize: 13, color: C.txM, marginBottom: 14, lineHeight: 1.5 }}>On upload: automatic alignment + signature extraction + stamp detection. Only <b>customer</b> signatures are listed.</p>
              <UploadZone accept="PDF, JPEG, PNG, TIFF" onFiles={handleUploadDoc} /></>
          ) : addMode === "workspace" && hasSigs ? (
            <><h3 style={{ fontFamily: "'Outfit'", fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Select Signature</h3><div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{signatures.map(s => <SigCard key={s.sigNo} sig={s} selected={selectedSigs.includes(s.sigNo)} onClick={() => setSelectedSigs([s.sigNo])} imgOverride={sigCleanCache[`ws_${s.sigNo}`]} />)}</div></>
          ) : uploadedSig ? (
            <><h3 style={{ fontFamily: "'Outfit'", fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Uploaded Signature</h3>
            <div style={{ textAlign: "center", padding: 16, background: C.off, borderRadius: 8, border: `1px solid ${C.bd}` }}>
              <img src={getCleanImg(uploadedSig, "add_upload")} alt="Uploaded" style={{ maxWidth: "100%", maxHeight: 200, objectFit: "contain", borderRadius: 6 }} />
              <div style={{ fontSize: 12, color: C.txM, marginTop: 8 }}>{uploadedSig.name}</div>
            </div>
            <button onClick={() => setUploadedSig(null)} style={{ marginTop: 12, width: "100%", padding: 8, background: C.off, border: `1px solid ${C.bd}`, borderRadius: 8, fontSize: 13, color: C.txM, cursor: "pointer" }}>Change File</button></>
          ) : (
            <><h3 style={{ fontFamily: "'Outfit'", fontSize: 17, fontWeight: 700, marginBottom: 16 }}>Upload Signature</h3><UploadZone accept="JPEG, PNG, PDF" onFiles={handleUploadNew} /></>
          )}
        </div>
        <div style={{ background: C.wh, borderRadius: 12, padding: 24, border: `1px solid ${C.bd}` }}>
          <h3 style={{ fontFamily: "'Outfit'", fontSize: 17, fontWeight: 700, marginBottom: 16 }}>Details</h3>
          {[{ l: "Customer ID *", k: "customer_id", p: "Unique ID" }, { l: "TCKNO", k: "tckno", p: "11 digits" }, { l: "VKN", k: "vkn", p: "10 digits" }, { l: "Name", k: "name", p: "Customer name" }].map((f, i) => <div key={i} style={{ marginBottom: 14 }}><label style={{ fontSize: 13, fontWeight: 600, color: C.txS, display: "block", marginBottom: 5 }}>{f.l}</label><input value={fields[f.k] || ""} onChange={e => setFields(prev => ({ ...prev, [f.k]: f.k === "tckno" ? e.target.value.replace(/\D/g, "").slice(0, 11) : f.k === "vkn" ? e.target.value.replace(/\D/g, "").slice(0, 10) : e.target.value }))} placeholder={f.p} style={{ width: "100%", padding: "10px 12px", background: C.off, border: `1px solid ${C.bd}`, borderRadius: 8, fontSize: 14, color: C.tx }} /></div>)}
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: C.txS, display: "block", marginBottom: 5 }}>Cleaning Before Save</label>
            {addMode === "document" ? <div style={{ padding: "10px 12px", background: `${C.ok}0c`, border: `1px solid ${C.ok}30`, borderRadius: 8, fontSize: 13, color: C.txS }}>Automatic — <b>stamp</b> cleaning for signatures overlapping a stamp, <b>basic</b> for the rest.</div> :
            <select value={addCleaning} onChange={e => setAddCleaning(e.target.value)} style={{ width: "100%", padding: "10px 12px", background: C.off, border: `1px solid ${C.bd}`, borderRadius: 8, fontSize: 14, color: C.tx }}>
              <option value="none">None (save as-is)</option>
              <option value="basic">Basic Clean (recommended)</option>
              <option value="stamp">Stamp Clean</option>
            </select>}
          </div>
          <Btn sx={{ width: "100%" }} onClick={() => { if (currentSig) handleAdd(currentSig); else notify("Please select or upload a signature first", "warning"); }}
            ch={addingSigNo ? <><div style={{ width: 18, height: 18, border: "2px solid rgba(255,255,255,.3)", borderTopColor: C.wh, borderRadius: "50%", animation: "spin .7s linear infinite" }} /> Saving...</> : <>{ic.plus(C.wh)} Register Signature</>} />
        </div>
      </div>
    </P>;
  };

  // ═══ STAMP DETECTION ═══
  const StampDetection = () => {
    const [file, setFile] = useState(_stampDetCache.file);
    const [preview, setPreview] = useState(_stampDetCache.preview);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(_stampDetCache.result);
    const [selectedPage, setSelectedPage] = useState(1);
    // Persist across remounts (background ops re-render the parent → this tab remounts).
    useEffect(() => { _stampDetCache = { file, preview, result }; }, [file, preview, result]);

    const handleUpload = async (files) => {
      const f = files[0];
      setFile(f);
      setResult(null);
      setSelectedPage(1);
      if (f.type && f.type.startsWith("image/")) {
        const dataUrl = await new Promise(r => { const reader = new FileReader(); reader.onload = () => r(reader.result); reader.readAsDataURL(f); });
        setPreview(dataUrl);
      } else {
        setPreview(null);
      }
    };

    const detectStamps = async () => {
      if (!file) { notify("Upload a document first", "warning"); return; }
      setBusy(true); setResult(null);
      const formData = new FormData();
      formData.append("file", file);
      try {
        const res = await fetch(`${API_BASE}/api/detect_stamps`, { method: "POST", body: formData });
        if (!res.ok) throw new Error("Detection failed");
        const data = await res.json();
        setResult(data);
        notify(`Detected ${data.stamp_count || 0} stamp(s)`, "success");
      } catch (e) { notify(e.message, "error"); }
      setBusy(false);
    };

    const autoStamps = workspace.stamps || [];
    const effResult = result || (autoStamps.length > 0 ? { stamps: autoStamps, stamp_count: autoStamps.length, page_count: workspace.document?.pageCount || 1, auto: true } : null);
    const stamps = effResult?.stamps || [];
    const docObj = file ? { name: file.name, type: file.type, pageCount: (file.type && file.type.startsWith("image/")) ? 1 : (result?.page_count || 0), dataUrl: preview } : null;

    return <P title="Stamp Detection" sub="Detect stamps and seals in documents">
      {!result && workspace.stampsDetecting && <div style={{ display: "flex", alignItems: "center", gap: 10, background: C.priGh || C.off, border: `1px solid ${C.bd}`, borderRadius: 10, padding: "12px 16px", marginBottom: 16 }}>
        <div style={{ width: 18, height: 18, border: `2px solid ${C.bd}`, borderTopColor: C.pri, borderRadius: "50%", animation: "spin .7s linear infinite" }} />
        <span style={{ fontSize: 14, color: C.txS }}>Detecting stamps in the extracted document...</span>
      </div>}
      {!result && effResult?.auto && <div style={{ display: "flex", alignItems: "center", gap: 8, background: `${C.ok}0c`, border: `1px solid ${C.ok}30`, borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: C.ok, fontWeight: 600 }}>{ic.check ? ic.check(C.ok) : "✓"} Auto-detected after extraction — results below. Upload another document on the left to run it separately.</div>}
      <div className="g1-m" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div style={{ background: C.wh, borderRadius: 12, padding: 24, border: `1px solid ${C.bd}` }}>
          <h3 style={{ fontFamily: "'Outfit'", fontSize: 17, fontWeight: 700, marginBottom: 16 }}>Upload Document</h3>
          {file ? <>
            <div style={{ marginBottom: 16 }}>
              <DocPreview doc={docObj} signatures={[]} selectedPage={selectedPage} onPageChange={setSelectedPage} aligned={false} />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => { setFile(null); setPreview(null); setResult(null); setSelectedPage(1); }} style={{ flex: 1, padding: 8, background: C.off, border: `1px solid ${C.bd}`, borderRadius: 8, fontSize: 13, color: C.txM, cursor: "pointer" }}>Change</button>
              <Btn sx={{ flex: 2 }} onClick={detectStamps}
                ch={busy ? <><div style={{ width: 18, height: 18, border: "2px solid rgba(255,255,255,.3)", borderTopColor: C.wh, borderRadius: "50%", animation: "spin .7s linear infinite" }} /> Detecting...</> : <>{ic.sclean(C.wh)} Detect Stamps</>} />
            </div>
          </> : <UploadZone accept="PDF, JPEG, PNG, TIFF" onFiles={handleUpload} />}
        </div>

        <div style={{ background: C.wh, borderRadius: 12, padding: 24, border: `1px solid ${C.bd}` }}>
          <h3 style={{ fontFamily: "'Outfit'", fontSize: 17, fontWeight: 700, marginBottom: 16 }}>Detection Results</h3>
          {effResult ? <>
            <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
              <div style={{ background: C.off, borderRadius: 8, padding: "10px 16px", flex: 1, textAlign: "center" }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: C.pri }}>{effResult.stamp_count || 0}</div>
                <div style={{ fontSize: 12, color: C.txM }}>Stamps Found</div>
              </div>
              <div style={{ background: C.off, borderRadius: 8, padding: "10px 16px", flex: 1, textAlign: "center" }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: C.pri }}>{effResult.page_count || 1}</div>
                <div style={{ fontSize: 12, color: C.txM }}>Pages</div>
              </div>
              {effResult.elapsed_sec && <div style={{ background: C.off, borderRadius: 8, padding: "10px 16px", flex: 1, textAlign: "center" }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: C.pri }}>{effResult.elapsed_sec}s</div>
                <div style={{ fontSize: 12, color: C.txM }}>Duration</div>
              </div>}
            </div>
            {stamps.length > 0 ? <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {stamps.map((s, i) => <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: 12, background: C.off, borderRadius: 8, border: `1px solid ${C.bd}` }}>
                {s.data && <img src={s.data} alt={s.type} style={{ width: 100, height: 70, objectFit: "contain", borderRadius: 6, background: C.wh, border: `1px solid ${C.bd}` }} />}
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.tx }}>{s.type === "daire_muhur" ? "Round Seal" : s.type === "yazi_kase" ? "Text Stamp" : s.type}</div>
                  <div style={{ fontSize: 12, color: C.txM, marginTop: 2 }}>Page {s.page}</div>
                  <div style={{ fontSize: 11, color: C.txD, marginTop: 2 }}>bbox: [{s.bbox?.join(", ")}]</div>
                </div>
              </div>)}
            </div> : <div style={{ textAlign: "center", padding: 32, color: C.txM }}>No stamps or seals detected in this document</div>}
          </> : <div style={{ textAlign: "center", padding: 40 }}>
            {workspace.stampsDetecting ? <><div style={{ width: 40, height: 40, border: `3px solid ${C.bd}`, borderTopColor: C.pri, borderRadius: "50%", animation: "spin .7s linear infinite", margin: "0 auto 16px" }} /><p style={{ color: C.txM, fontSize: 16 }}>Detecting stamps...</p></> :
            <><div style={{ marginBottom: 16, transform: "scale(2.5)", opacity: 0.5, display: "inline-block" }}>{ic.sclean(C.txD)}</div>
            <p style={{ color: C.txM, fontSize: 16, marginTop: 12 }}>Upload a document to detect stamps and seals</p></>}
          </div>}
        </div>
      </div>
    </P>;
  };

  // ═══ DOCUMENT TYPE DETECTION ═══
  const DocType = () => {
    const [file, setFile] = useState(_docTypeCache.file);
    const [preview, setPreview] = useState(_docTypeCache.preview);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(_docTypeCache.result);
    const [selectedPage, setSelectedPage] = useState(1);
    // Persist across remounts (background ops re-render the parent → this tab remounts).
    useEffect(() => { _docTypeCache = { file, preview, result }; }, [file, preview, result]);

    const loadFile = (f) => {
      setFile(f); setResult(null); setSelectedPage(1);
      if (f.type.startsWith("image/")) { const r = new FileReader(); r.onload = () => setPreview(r.result); r.readAsDataURL(f); }
      else setPreview(null);
    };

    const classify = async () => {
      if (!file) { notify("Upload a document first", "warning"); return; }
      setBusy(true); setResult(null);
      const formData = new FormData();
      formData.append("pdf", file);
      formData.append("filename", file.name);
      if (user.companyId) formData.append("company_id", user.companyId);
      if (user.id) formData.append("user_id", user.id);
      try {
        const res = await fetch(`${API_BASE}/api/classify_document`, { method: "POST", body: formData });
        if (!res.ok) throw new Error("Classification failed");
        const data = await res.json();
        setResult(data);
        notify("Document classified", "success");
      } catch (e) { notify("Classification failed: " + e.message, "error"); }
      setBusy(false);
    };

    const cls = result?.classification;
    const detType = cls ? (cls.document_class || "unknown") : null;
    const scores = cls?.all_scores || {};
    const predictions = cls
      ? (Object.keys(scores).length > 0
          ? Object.entries(scores).map(([label, score]) => ({ label, score: Number(score) || 0 })).sort((a, b) => b.score - a.score)
          : [{ label: detType, score: cls.confidence || 0 }])
      : [];
    const docObj = file ? { name: file.name, type: file.type, pageCount: file.type.startsWith("image/") ? 1 : (result?.page_count || 0), dataUrl: preview } : null;

    return <P title="Document Type Detection" sub="Automatically classify the type of an uploaded document">
      <div className="g1-m" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {/* Left: Upload */}
        <div style={{ background: C.wh, borderRadius: 12, padding: 24, border: `1px solid ${C.bd}` }}>
          <h3 style={{ fontFamily: "'Outfit'", fontSize: 17, fontWeight: 700, marginBottom: 16 }}>Upload Document</h3>
          {file ? <>
            <div style={{ marginBottom: 16 }}>
              <DocPreview doc={docObj} signatures={[]} selectedPage={selectedPage} onPageChange={setSelectedPage} aligned={false} />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn sx={{ flex: 1 }} onClick={classify} ch={busy ? <><div style={{ width: 18, height: 18, border: "2px solid rgba(255,255,255,.3)", borderTopColor: C.wh, borderRadius: "50%", animation: "spin .7s linear infinite" }} /> Classifying...</> : <>{ic.docType(C.wh)} Detect Type</>} />
              <button onClick={() => { setFile(null); setPreview(null); setResult(null); setSelectedPage(1); }} style={{ padding: "0 14px", borderRadius: 8, border: `1px solid ${C.bd}`, background: C.wh, fontSize: 13, color: C.txM, cursor: "pointer" }}>Change</button>
            </div>
          </> : <UploadZone onFiles={f => loadFile(f[0])} />}
        </div>

        {/* Right: Result */}
        <div style={{ background: C.wh, borderRadius: 12, padding: 24, border: `1px solid ${C.bd}` }}>
          <h3 style={{ fontFamily: "'Outfit'", fontSize: 17, fontWeight: 700, marginBottom: 16 }}>Classification Result</h3>
          {result ? <div>
            {detType ? <>
              <div style={{ textAlign: "center", padding: "34px 0" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.txD, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 10 }}>Detected Type</div>
                <div style={{ fontFamily: "'Outfit'", fontSize: 32, fontWeight: 800, color: detType === "unknown" ? C.txM : C.pri }}>{detType === "unknown" ? "Undetermined" : detType}</div>
              </div>
            </> : <div style={{ textAlign: "center", padding: 40, color: C.txM }}>No classification returned</div>}
          </div> : <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 340 }}>
            {busy ? <><div style={{ width: 44, height: 44, border: `3px solid ${C.bd}`, borderTopColor: C.pri, borderRadius: "50%", animation: "spin .7s linear infinite", marginBottom: 16 }} /><p style={{ color: C.txM, fontSize: 16 }}>Analyzing document...</p></> :
            <><div style={{ marginBottom: 16, transform: "scale(2.5)", opacity: 0.5 }}>{ic.docType(C.txD)}</div><p style={{ color: C.txM, marginTop: 12, fontSize: 17 }}>Upload a document to detect its type</p></>}
          </div>}
        </div>
      </div>
    </P>;
  };

  // ═══ API SERVICES ═══
  const Docs = () => {
    const endpoints = [
      { m: "POST", p: "/api/extract_signatures", d: "Detect and extract signature locations from a document" },
      { m: "POST", p: "/api/clean_signature", d: "Clean a signature (remove lines, text, background)" },
      { m: "POST", p: "/api/compare_two_signatures", d: "Compare two signatures and return a similarity score" },
      { m: "POST", p: "/api/signature_comparison", d: "Compare a signature against a customer's registered set" },
      { m: "POST", p: "/api/compare_matrix", d: "Compare a set of signatures against another set" },
      { m: "POST", p: "/api/align_document", d: "Auto-correct document rotation / skew" },
      { m: "POST", p: "/api/classify_document", d: "Classify the type of a document" },
      { m: "POST", p: "/api/add_signature", d: "Register a reference signature to the database" },
      { m: "GET", p: "/api/signatures", d: "List / search registered signatures" },
      { m: "PUT", p: "/api/signatures/{id}", d: "Update a registered signature" },
      { m: "DELETE", p: "/api/signatures/{id}", d: "Delete a registered signature" },
      { m: "GET", p: "/api/dashboard/stats", d: "Aggregated dashboard statistics" },
      { m: "GET", p: "/api/activity_log", d: "Recent activity log entries" },
    ];
    const mColor = (m) => m === "GET" ? C.ok : m === "POST" ? C.pri : m === "PUT" ? C.warn : C.err;
    return <P title="API Services" sub="Available signature verification endpoints">
      <div style={{ background: C.wh, borderRadius: 12, border: `1px solid ${C.bd}`, overflow: "hidden" }}>
        {endpoints.map((e, i) => <div key={i} style={{ padding: "14px 18px", borderBottom: i < endpoints.length - 1 ? `1px solid ${C.bdLt}` : "none", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, fontWeight: 700, fontFamily: "'JetBrains Mono'", color: mColor(e.m), background: `${mColor(e.m)}12`, padding: "3px 10px", borderRadius: 6, minWidth: 62, textAlign: "center" }}>{e.m}</span>
          <span style={{ fontSize: 14, fontWeight: 600, fontFamily: "'JetBrains Mono'", color: C.tx }}>{e.p}</span>
          <span style={{ fontSize: 13, color: C.txM, flex: 1, minWidth: 200 }}>{e.d}</span>
        </div>)}
      </div>
      <p style={{ fontSize: 13, color: C.txM, marginTop: 14 }}>Base URL: <span style={{ fontFamily: "'JetBrains Mono'", color: C.pri }}>{API_BASE || window.location.origin}</span></p>
    </P>;
  };

  // ═══ ACTIVITY LOG ═══
  const Activity = () => {
    return <P title="Activity Log" sub="Full history of processing operations">
      <div style={{ background: C.wh, borderRadius: 12, border: `1px solid ${C.bd}`, overflow: "hidden" }}>
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${C.bd}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ fontFamily: "'Outfit'", fontSize: 15, fontWeight: 700 }}>{activities.length} operation{activities.length !== 1 ? "s" : ""}</h3>
          <button onClick={() => fetchActivities(50)} style={{ fontSize: 13, color: C.pri, background: "none", border: "none", fontWeight: 600, cursor: "pointer" }}>{actLoading ? "Refreshing..." : "Refresh"}</button>
        </div>
        <JT d={activities} />
      </div>
    </P>;
  };

  // ═══ ALERTS ═══
  const Alerts = () => {
    // Alerts = operations that didn't complete cleanly (errors / still processing)
    const alerts = activities.filter(a => a.status && a.status !== "completed");
    const sev = (s) => s === "error" || s === "failed" ? { c: C.err, label: "Error" } : s === "processing" ? { c: C.warn, label: "In Progress" } : { c: C.txD, label: s };
    return <P title="Alerts" sub="Operations that need your attention">
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 12, marginBottom: 20 }}>
        {[{ l: "Open Alerts", v: alerts.length, c: alerts.length > 0 ? C.err : C.ok }, { l: "Errors", v: activities.filter(a => a.status === "error" || a.status === "failed").length, c: C.err }].map((s, i) => <div key={i} style={{ background: C.wh, borderRadius: 12, padding: 20, border: `1px solid ${C.bd}` }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.txM, marginBottom: 6 }}>{s.l}</div>
          <div style={{ fontFamily: "'Outfit'", fontSize: 34, fontWeight: 800, color: s.c }}>{s.v}</div>
        </div>)}
      </div>
      <div style={{ background: C.wh, borderRadius: 12, border: `1px solid ${C.bd}`, overflow: "hidden" }}>
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${C.bd}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ fontFamily: "'Outfit'", fontSize: 15, fontWeight: 700 }}>Recent Alerts</h3>
          <button onClick={() => fetchActivities(100)} style={{ fontSize: 13, color: C.pri, background: "none", border: "none", fontWeight: 600, cursor: "pointer" }}>{actLoading ? "Refreshing..." : "Refresh"}</button>
        </div>
        {alerts.length === 0 ? <div style={{ padding: 48, textAlign: "center" }}>
          <div style={{ fontSize: 44, marginBottom: 12 }}>✅</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: C.ok, marginBottom: 4 }}>All clear</div>
          <div style={{ fontSize: 14, color: C.txM }}>No failed or pending operations right now.</div>
        </div> : <div>
          {alerts.map((a, i) => { const s = sev(a.status); return <div key={i} style={{ padding: "14px 18px", borderBottom: i < alerts.length - 1 ? `1px solid ${C.bdLt}` : "none", display: "flex", alignItems: "center", gap: 14 }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", background: s.c, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: C.tx }}>{actTypeLabel(a.action_type)} — {a.document_name || "—"}</div>
              <div style={{ fontSize: 12, color: C.txM, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.details || a.error || "No further detail"}</div>
            </div>
            <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 6, color: s.c, background: `${s.c}12`, flexShrink: 0 }}>{s.label}</span>
            <span style={{ fontSize: 12, color: C.txD, flexShrink: 0 }}>{a.created_at ? new Date(a.created_at).toLocaleString("tr-TR", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" }) : ""}</span>
          </div>; })}
        </div>}
      </div>
    </P>;
  };

  // ═══ REPORTS ═══
  const Reports = () => {
    const byType = {};
    activities.forEach(a => { byType[a.action_type] = (byType[a.action_type] || 0) + 1; });
    const typeRows = Object.entries(byType).sort((a, b) => b[1] - a[1]);
    const maxCount = typeRows.length > 0 ? Math.max(...typeRows.map(r => r[1])) : 1;
    const okCount = activities.filter(a => a.status === "completed").length;
    const successRate = activities.length > 0 ? Math.round((okCount / activities.length) * 100) : 0;
    const typeColor = { extraction: C.pri, comparison: C.ok, cleaning: C.warn, classification: C.acc, alignment: C.purp, forgery_check: C.err, batch: C.org };

    return <P title="Reports" sub="Usage summary and operation breakdown">
      {/* Summary cards */}
      <div className="stat-g" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, marginBottom: 24 }}>
        {[{ l: "Total Signatures", v: dashStats ? dashStats.total_signatures.toLocaleString() : "—", c: C.pri }, { l: "Operations (30d)", v: dashStats ? dashStats.total_operations.toLocaleString() : "—", c: C.ok }, { l: "Documents", v: dashStats ? dashStats.documents_processed.toLocaleString() : "—", c: C.acc }, { l: "Success Rate", v: `${successRate}%`, c: successRate >= 90 ? C.ok : successRate >= 70 ? C.warn : C.err }].map((s, i) => <div key={i} style={{ background: C.wh, borderRadius: 14, padding: 24, border: `1px solid ${C.bd}` }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.txM, marginBottom: 8 }}>{s.l}</div>
          <div style={{ fontFamily: "'Outfit'", fontSize: 34, fontWeight: 800, color: s.c }}>{s.v}</div>
        </div>)}
      </div>

      {/* Operation breakdown */}
      <div style={{ background: C.wh, borderRadius: 12, padding: 22, border: `1px solid ${C.bd}`, marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
          <h3 style={{ fontFamily: "'Outfit'", fontSize: 18, fontWeight: 700 }}>Operations by Type</h3>
          <span style={{ fontSize: 13, color: C.txM }}>last {activities.length} record{activities.length !== 1 ? "s" : ""}</span>
        </div>
        {typeRows.length === 0 ? <div style={{ padding: 30, textAlign: "center", color: C.txM, fontSize: 14 }}>No data yet</div> :
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {typeRows.map(([type, count], i) => { const col = typeColor[type] || C.pri; return <div key={i}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: C.tx }}>{actTypeEmoji(type)} {actTypeLabel(type)}</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: col }}>{count}</span>
            </div>
            <div style={{ height: 10, background: C.bdLt, borderRadius: 5, overflow: "hidden" }}>
              <div style={{ width: `${Math.round((count / maxCount) * 100)}%`, height: "100%", background: col, borderRadius: 5 }} />
            </div>
          </div>; })}
        </div>}
      </div>

      {/* Export */}
      <div style={{ background: C.wh, borderRadius: 12, padding: 20, border: `1px solid ${C.bd}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h3 style={{ fontFamily: "'Outfit'", fontSize: 16, fontWeight: 700, color: C.tx }}>Export report</h3>
          <p style={{ fontSize: 13, color: C.txM, marginTop: 2 }}>Download the current activity breakdown as JSON.</p>
        </div>
        <Btn onClick={() => downloadJSON({ generated_at: new Date().toISOString(), stats: dashStats, by_type: byType, success_rate: successRate }, `qsign_report_${Date.now()}.json`)} ch={<>{ic.download(C.wh)} Export JSON</>} />
      </div>
    </P>;
  };

  // ═══ SETTINGS ═══
  const Settings = () => {
    const rows = [
      { l: "Full Name", v: user.fullName },
      { l: "Email", v: user.email },
      { l: "Company", v: user.companyName },
      { l: "Company Code", v: user.companyCode },
      { l: "Branch", v: user.branchName || "—" },
      { l: "Role", v: user.roleName || "—" },
      { l: "Last Login", v: user.lastLogin || "—" },
    ];
    return <P title="Settings" sub="Account and workspace information">
      <div className="g1-m" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div style={{ background: C.wh, borderRadius: 12, padding: 24, border: `1px solid ${C.bd}` }}>
          <h3 style={{ fontFamily: "'Outfit'", fontSize: 17, fontWeight: 700, marginBottom: 16 }}>Profile</h3>
          {rows.map((r, i) => <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "11px 0", borderBottom: i < rows.length - 1 ? `1px solid ${C.bdLt}` : "none" }}>
            <span style={{ fontSize: 14, color: C.txM }}>{r.l}</span>
            <span style={{ fontSize: 14, fontWeight: 600, color: C.tx, textAlign: "right" }}>{r.v}</span>
          </div>)}
        </div>
        <div style={{ background: C.wh, borderRadius: 12, padding: 24, border: `1px solid ${C.bd}` }}>
          <h3 style={{ fontFamily: "'Outfit'", fontSize: 17, fontWeight: 700, marginBottom: 16 }}>Session</h3>
          <p style={{ fontSize: 14, color: C.txM, lineHeight: 1.6, marginBottom: 16 }}>You are signed in to the AIATUS QSign workspace. Use the button below to end your session.</p>
          <Btn v="out" onClick={() => { localStorage.clear(); if (go) go("login"); else window.location.reload(); }} ch={<>{ic.logout ? ic.logout(C.pri) : null} Log Out</>} />
        </div>
      </div>
    </P>;
  };

  // ═══ HELP ═══
  const Help = () => {
    const faqs = [
      { q: "How do I extract signatures from a document?", a: "Open the Extraction page, upload a PDF or image, then click “Extract Signatures”. The document is aligned automatically first. Detected signatures appear with bounding boxes — green for customer signatures, blue for institution signatures — and can be exported as JSON or CSV." },
      { q: "Which file formats are supported?", a: "PDF (multi-page), PNG, JPG/JPEG and TIFF. For batch processing you can also select an entire folder." },
      { q: "What does “Signature Cleaning” do?", a: "It removes background lines, stamps and text around a signature so comparisons are more accurate. Basic Cleaning handles lines/text; Stamp Cleaning separates a signature from an overlapping stamp." },
      { q: "What is Smart Cleaning?", a: "After extraction, stamps are detected on the same aligned pages as the signatures. Each signature is then cleaned with the model it actually needs: if its bounding box overlaps a detected stamp, Stamp Cleaning is applied; otherwise Basic Cleaning is applied. The chosen model is shown on every signature card in the Extraction page (🧹 Stamp clean / 🧹 Basic clean) and is also included in the JSON export as cleaning_method. The same choice is reused when comparing signatures and when registering them from a document, so you never have to pick a cleaning model by hand." },
      { q: "When do I choose the cleaning model myself?", a: "Only when you upload a brand-new signature image manually in Compare → Upload & Compare. Signatures coming from a document (or already stored in the database) use Smart Cleaning automatically. The Original / Cleaned toggle in Compare only changes what you see — it never changes the comparison result." },
      { q: "How does comparison work?", a: "You can compare a signature against the database (by Customer ID), against another signature in the same document, across two documents, or against a signature you upload." },
      { q: "My document is scanned crooked — what can I do?", a: "Nothing extra is needed: alignment is applied automatically before extraction, stamp detection and absence checks. The Document Alignment page also lets you align a document on its own and download the corrected pages." },
      { q: "How do I register a reference signature?", a: "Select a signature, open Add Signature, fill in at least one identifier (Customer ID, TCKNO or VKN) and click Register." },
    ];
    return <P title="Help" sub="Frequently asked questions and usage guidance">
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
        {faqs.map((f, i) => <div key={i} style={{ background: C.wh, borderRadius: 12, padding: "18px 22px", border: `1px solid ${C.bd}` }}>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
            <span style={{ flexShrink: 0, marginTop: 2 }}>{helpIcon(C.pri)}</span>
            <div>
              <h3 style={{ fontFamily: "'Outfit'", fontSize: 16, fontWeight: 700, color: C.tx, marginBottom: 6 }}>{f.q}</h3>
              <p style={{ fontSize: 14, color: C.txM, lineHeight: 1.65 }}>{f.a}</p>
            </div>
          </div>
        </div>)}
      </div>
      <div style={{ background: C.priGh || C.off, borderRadius: 12, padding: 22, border: `1px solid ${C.bd}`, marginTop: 16 }}>
        <h3 style={{ fontFamily: "'Outfit'", fontSize: 16, fontWeight: 700, color: C.tx, marginBottom: 8 }}>Still need help?</h3>
        <p style={{ fontSize: 14, color: C.txM, lineHeight: 1.65 }}>Contact the AIATUS support team at <a href="mailto:info@aiatus.com" style={{ color: C.pri, fontWeight: 600, textDecoration: "none" }}>info@aiatus.com</a>. Please include the document name and the operation you were performing so we can assist faster.</p>
      </div>
    </P>;
  };

  // ═══ POLICY ═══
  const Policy = () => {
    const sections = [
      { t: "Data Processing", b: "Documents you upload are processed solely to perform the signature operation you request (extraction, cleaning, comparison, alignment or classification). Processing takes place within the AIATUS QSign environment provisioned for your organization." },
      { t: "Storage & Retention", b: "Reference signatures you explicitly register are stored in your organization's signature database. Uploaded working documents and their derived page images are retained only as long as needed to complete and display the requested operation, in line with your organization's retention configuration." },
      { t: "Access & Confidentiality", b: "Access is restricted to authenticated users within your company and branch scope. Signature data and document content are treated as confidential and are not shared with third parties outside the agreed service." },
      { t: "Acceptable Use", b: "The platform must be used only for legitimate signature verification purposes. Attempting to process documents you are not authorized to handle, or using results to mislead, is prohibited." },
      { t: "Accuracy Disclaimer", b: "Signature detection, cleaning and comparison are AI-assisted and provided to support human review. Similarity results and classifications are indicative and should not be treated as a sole legal determination of authenticity." },
      { t: "Security", b: "Transport is protected in transit and access is authenticated per session. Log out when you finish, especially on shared devices, to protect your workspace." },
    ];
    return <P title="Policy" sub="Usage, privacy and data handling policy">
      <div style={{ background: C.wh, borderRadius: 12, border: `1px solid ${C.bd}`, overflow: "hidden" }}>
        {sections.map((s, i) => <div key={i} style={{ padding: "20px 24px", borderBottom: i < sections.length - 1 ? `1px solid ${C.bdLt}` : "none" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <span style={{ width: 26, height: 26, borderRadius: 7, background: `${C.pri}12`, color: C.pri, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, fontFamily: "'Outfit'" }}>{i + 1}</span>
            <h3 style={{ fontFamily: "'Outfit'", fontSize: 17, fontWeight: 700, color: C.tx }}>{s.t}</h3>
          </div>
          <p style={{ fontSize: 14, color: C.txM, lineHeight: 1.7, paddingLeft: 36 }}>{s.b}</p>
        </div>)}
      </div>
      <p style={{ fontSize: 12, color: C.txD, marginTop: 14 }}>© {new Date().getFullYear()} AIATUS · QSign. This summary is provided for convenience; your organization's signed service agreement prevails in case of conflict.</p>
    </P>;
  };

  // ─── TABS MAP ───
  // BatchTab kept mounted (via a stable element) to preserve its state across tab switches
  const batchRef = <BatchTab notify={notify} />;

  const tabs = {
    home: <Home />,
    dashboard: <Dashboard />,
    alerts: <Alerts />,
    reports: <Reports />,
    extract: <Extract />,
    absence: <SignatureAbsenceTab notify={notify} />,
    compare: <CompareTab notify={notify} workspaceSigs={workspace.signatures} workspaceStamps={workspace.stamps} parentCleanCache={sigCleanCache} />,
    bclean: <CleanTab key="bclean" notify={notify} workspaceSigs={workspace.signatures} selectedSigNos={selectedSigs} method="model_s" title="Basic Signature Cleaning" subtitle="Remove lines, text and background from signatures" />,
    sclean: <CleanTab key="sclean" notify={notify} workspaceSigs={workspace.signatures} selectedSigNos={selectedSigs} method="stamp" title="Stamp Cleaning" subtitle="Separate signatures from overlapping stamps" />,
    stampdet: <StampDetection />,
    addsig: <AddSig />,
    search: <SearchTab user={user} notify={notify} />,
    doctype: <DocType />,
    align: <AlignTab notify={notify} />,
    batch: batchRef,
    activity: <Activity />,
    help: <Help />,
    policy: <Policy />,
    settings: <Settings />,
  };

  // ─── HEADER quick buttons ───
  const quick = [
    { id: "home", i: ic.home, l: "Home", c: C.pri },
    { id: "dashboard", i: dashboardIcon, l: "Dashboard", c: C.pri },
    { id: "extract", i: ic.detect, l: "Extract", c: C.pri },
    { id: "compare", i: ic.compare, l: "Compare", c: C.ok },
    { id: "batch", i: ic.batch, l: "Batch", c: C.org },
  ];

  const initials = ((user.firstName || "U")[0] + (user.lastName || "")[0]).toUpperCase();

  // ═══════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════
  return (
    <div style={{ minHeight: "100vh", background: C.bg, fontFamily: "'Inter', system-ui, sans-serif" }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fi { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 8px; height: 8px; }
        ::-webkit-scrollbar-thumb { background: ${C.bd}; border-radius: 4px; }
        @media (max-width: 900px) {
          .desk-aside { display: none !important; }
          .desk-main { margin-left: 0 !important; }
          .desk-footer { left: 0 !important; }
          .g1-m { grid-template-columns: 1fr !important; }
          .stat-g { grid-template-columns: repeat(2,1fr) !important; }
          .hdr-quick { display: none !important; }
        }
        @media (min-width: 901px) { .mob-btn { display: none !important; } }
      `}</style>

      {/* ─── TOP BAR (navy) ─── */}
      <header style={{ position: "fixed", top: 0, left: 0, right: 0, height: 62, background: NAVY, borderBottom: `1px solid ${NAVY_BORDER}`, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 20px", zIndex: 100 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <button className="mob-btn" onClick={() => setMob(true)} style={{ background: "rgba(255,255,255,.12)", border: "none", borderRadius: 8, padding: 8, cursor: "pointer", display: "flex" }}>{ic.menu(C.wh)}</button>
          <img src={LOGO_SRC} alt="AIATUS" height={30} style={{ display: "block" }} />
          <div style={{ width: 1, height: 26, background: "rgba(255,255,255,.25)" }} />
          <span style={{ fontFamily: "'Outfit'", fontSize: 20, fontWeight: 800, color: C.wh, letterSpacing: -0.3 }}>QSign</span>
        </div>

        {/* Quick nav pills */}
        <div className="hdr-quick" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {quick.map(q => { const active = tab === q.id; return <button key={q.id} onClick={() => setTab(q.id)} style={{ display: "flex", alignItems: "center", gap: 7, padding: "7px 14px", borderRadius: 9, border: "none", background: active ? C.wh : "rgba(255,255,255,.12)", color: active ? q.c : C.wh, fontSize: 14, fontWeight: 600, cursor: "pointer", transition: "all .15s" }}>{q.i(active ? q.c : C.wh)} {q.l}</button>; })}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: C.wh, color: NAVY, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 800, fontFamily: "'Outfit'" }}>{initials}</div>
            <div className="hdr-quick" style={{ lineHeight: 1.2 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: C.wh }}>{user.fullName}</div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,.7)" }}>{user.email}</div>
            </div>
          </div>
          <button onClick={() => { localStorage.clear(); if (go) go("login"); else window.location.reload(); }} title="Log out" style={{ background: "rgba(255,255,255,.12)", border: "none", borderRadius: 8, padding: 8, cursor: "pointer", display: "flex" }}>{ic.logout ? ic.logout(C.wh) : ic.x(C.wh)}</button>
        </div>
      </header>

      {/* ─── MOBILE DRAWER ─── */}
      {mob && <>
        <div onClick={() => setMob(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 200 }} />
        <aside style={{ position: "fixed", top: 62, left: 0, bottom: 0, width: 260, background: `linear-gradient(180deg, ${NAVY}, ${NAVY_DK})`, zIndex: 201, overflowY: "auto", paddingBottom: 20, boxShadow: "4px 0 24px rgba(0,0,0,.2)" }}>
          <SB onClick={() => setMob(false)} />
        </aside>
      </>}

      {/* ─── DESKTOP SIDEBAR (navy) ─── */}
      <aside className="desk-aside" style={{ position: "fixed", top: 62, left: 0, bottom: 0, width: 218, background: `linear-gradient(180deg, ${NAVY}, ${NAVY_DK})`, overflowY: "auto", zIndex: 90, paddingBottom: 20 }}>
        <SB />
      </aside>

      {/* ─── MAIN ─── */}
      <main className="desk-main" style={{ marginLeft: 218, paddingTop: 62, minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        <div style={{ flex: 1, padding: "28px 32px 80px", maxWidth: 1500, width: "100%", margin: "0 auto" }}>
          {tabs[tab] || <Home />}
        </div>

        {/* ─── FOOTER (fixed at bottom, aligned to content area) ─── */}
        <footer className="desk-footer" style={{ position: "fixed", bottom: 0, left: 218, right: 0, background: `linear-gradient(90deg, ${NAVY}, ${NAVY_DK})`, color: C.wh, padding: "13px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, zIndex: 95, borderTop: `1px solid ${NAVY_BORDER}` }}>
          <span style={{ fontSize: 13, color: "rgba(255,255,255,.85)" }}>© {new Date().getFullYear()} AIATUS · QSign — Signature Verification Platform</span>
          <span style={{ fontSize: 12, color: "rgba(255,255,255,.6)", fontFamily: "'JetBrains Mono'" }}><Clock /></span>
        </footer>
      </main>

      <ToastHost />
    </div>
  );
}